import React, { useCallback, useEffect, useRef, useState } from 'react';

type DropZoneProps = {
  className: string;
  label: string;
  onDrop: (blockType: string) => void;
  suppressed?: boolean;
};
function DropZone({ className, label, onDrop, suppressed }: DropZoneProps) {
  const [dragging, setDragging] = useState(false);
  const [active, setActive] = useState(false);

  useEffect(() => {
    const onStart = () => setDragging(true);
    const onEnd = () => {
      setDragging(false);
      setActive(false);
    };
    document.addEventListener('dragstart', onStart);
    document.addEventListener('dragend', onEnd);
    return () => {
      document.removeEventListener('dragstart', onStart);
      document.removeEventListener('dragend', onEnd);
    };
  }, []);

  return (
    <div
      className={className}
      data-dragging={dragging && !suppressed ? 'true' : undefined}
      data-active={active ? 'true' : undefined}
      onDragEnter={() => setActive(true)}
      onDragLeave={() => setActive(false)}
      onDragOver={(e) => e.preventDefault()}
      onDrop={(e) => {
        e.preventDefault();
        setActive(false);
        const bt = e.dataTransfer.getData('blockType');
        if (bt) onDrop(bt);
      }}
    >
      <span className={styles.builderDropZoneInner}>{label}</span>
    </div>
  );
}

import jsPDF from 'jspdf';

import { InitialsPad, SessionInitials } from './InitialsPad';
import { SignaturePad } from './SignaturePad';
import styles from './ConsentFormRenderer.module.scss';
import { HostScrollTarget, TeleconsentMode } from 'context/teleconsent/context';

import {
  ConsentFormDefinition,
  ConsentSection,
  ContentBlock,
  FormField,
  FormState,
  SignatureCommit,
  SignatureState,
  SubmitReceipt,
} from '../types';
import { VARIABLE_REGISTRY, VariableContext, injectVariables } from '../variables/variableRegistry';
import { COUNTRIES } from '../forms/countries';
import { ClientViewport } from 'context/teleconsent/context';

export type BuilderModeConfig = {
  selectedBlockKey: string | null;
  onBlockClick: (sectionId: string, blockIndex: number) => void;
  onBlockDelete: (sectionId: string, blockIndex: number) => void;
  onBlockDuplicate: (sectionId: string, blockIndex: number) => void;
  onBlockMove: (sectionId: string, blockIndex: number, direction: 'up' | 'down') => void;
  onSectionDelete: (sectionId: string) => void;
  onSectionMove: (sectionId: string, direction: 'up' | 'down') => void;
  onSectionEdit?: (sectionId: string) => void;
  onDropBlock: (sectionId: string, blockType: string, atIndex: number) => void;
  onBlockDragReorder: (fromSectionId: string, fromBlockIndex: number, toSectionId: string, toIndex: number) => void;
  onNavLabelChange?: (sectionId: string, label: string) => void;
  totalSections: number;
  isMobilePreview?: boolean;
};

type Props = {
  definition: ConsentFormDefinition;
  providerName?: string;
  providerFirstName?: string;
  providerLastName?: string;
  providerTitle?: string;
  providerSuffix?: string;
  providerSpeciality?: string;
  providerRoomURL?: string;
  providerEmail?: string;
  organizationName?: string;
  clientLabel?: string;
  preview?: boolean;
  isBlankPreview?: boolean;
  builderConfig?: BuilderModeConfig;
  isHost?: boolean;
  mode?: TeleconsentMode;
  onModeChange?: (mode: TeleconsentMode) => void;
  onScroll?: (sectionId: string, sectionFraction: number) => void;
  hostScrollTarget?: HostScrollTarget | null;
  highlightedBlockId?: string | null;
  onHighlightBlock?: (blockId: string | null) => void;
  participantName?: string | null;
  onSubmit?: () => Promise<SubmitReceipt>;
  onClose?: () => void;
  onDownload?: () => void;
  onFieldChange?: (fieldId: string, value: string | boolean) => void;
  onApplySignature?: (dataUrl: string) => void;
  onGoBack?: () => void;
  goBackSignal?: number;
  externalFieldState?: FormState;
  externalSignature?: SignatureState;
  receipt?: SubmitReceipt | null;
  clientDownloaded?: boolean;
  clientViewport?: ClientViewport | null;
  clientEndedSession?: boolean;
  onDismissClientEnded?: () => void;
};

const today = () =>
  new Date().toLocaleDateString('en-US', {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  });

type DobFieldProps = {
  label: string;
  required?: boolean;
  value: string;
  onChange: (val: string) => void;
  applyLabel: (text: string) => string;
  invalid: boolean;
};

// ─── DateInputField ─────────────────────────────────────────────────────────

type DateInputFieldProps = {
  label: string;
  required?: boolean;
  value: string;
  onChange: (val: string) => void;
  applyLabel: (text: string) => string;
  invalid: boolean;
  defaultToToday?: boolean;
  lockField?: boolean;
};

function formatDateInput(raw: string): string {
  const digits = raw.replace(/\D/g, '').slice(0, 8);
  if (digits.length <= 2) return digits;
  if (digits.length <= 4) return `${digits.slice(0, 2)}/${digits.slice(2)}`;
  return `${digits.slice(0, 2)}/${digits.slice(2, 4)}/${digits.slice(4)}`;
}

function DateInputField({
  label,
  required,
  value,
  onChange,
  applyLabel,
  invalid,
  defaultToToday,
  lockField,
}: DateInputFieldProps) {
  useEffect(() => {
    if (defaultToToday && !value) {
      onChange(today());
    }
  }, []); // eslint-disable-line

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (lockField) return;
    const prev = value;
    const next = e.target.value;
    // If user is deleting, allow freely
    if (next.length < prev.length) {
      onChange(next.replace(/\/$/, ''));
      return;
    }
    onChange(formatDateInput(next));
  };

  return (
    <div
      className={[styles.field, styles.clientField, invalid ? styles.invalid : '', lockField ? styles.fieldLocked : '']
        .filter(Boolean)
        .join(' ')}
    >
      <label>
        {applyLabel(label)}
        {required && <span className={styles.req}>*</span>}
      </label>
      <input
        type='text'
        inputMode='numeric'
        placeholder='mm/dd/yyyy'
        value={value}
        onChange={handleChange}
        readOnly={lockField}
        maxLength={10}
        autoComplete='off'
        data-lpignore='true'
        data-form-type='other'
      />
      {invalid && <span className={styles.err}>Required to sign this consent.</span>}
    </div>
  );
}

const DOB_MONTHS = [
  'January',
  'February',
  'March',
  'April',
  'May',
  'June',
  'July',
  'August',
  'September',
  'October',
  'November',
  'December',
];

function parseDob(v: string) {
  if (!v) return { m: '', d: '', y: '' };
  const [yyyy, mm, dd] = v.split('-');
  return { m: mm ?? '', d: dd ?? '', y: yyyy ?? '' };
}

function DobField({ label, required, value, onChange, applyLabel, invalid }: DobFieldProps) {
  const init = parseDob(value);
  const [month, setMonth] = useState(init.m);
  const [day, setDay] = useState(init.d);
  const [year, setYear] = useState(init.y);

  // Sync local state when the value changes from external (SignalR) updates
  useEffect(() => {
    const parsed = parseDob(value);
    setMonth(parsed.m);
    setDay(parsed.d);
    setYear(parsed.y);
  }, [value]);

  const currentYear = new Date().getFullYear();
  const years = Array.from({ length: currentYear - 1899 }, (_, i) => String(currentYear - i));

  const update = (m: string, d: string, y: string) => {
    // Always broadcast so the other party sees each dropdown selection immediately.
    // Use empty string for unfilled parts; parseDob handles partial values gracefully.
    if (!m && !d && !y) {
      onChange('');
      return;
    }
    onChange(`${y}-${m ? m.padStart(2, '0') : ''}-${d ? d.padStart(2, '0') : ''}`);
  };

  return (
    <div
      className={[styles.field, styles.clientField, styles.dobField, invalid ? styles.invalid : '']
        .filter(Boolean)
        .join(' ')}
    >
      <label>
        {applyLabel(label)}
        {required && <span className={styles.req}>*</span>}
      </label>
      <div className={styles.dobSelects}>
        <div className={styles.dobSelectWrap}>
          <select
            value={month}
            onChange={(e) => {
              const v = e.target.value;
              setMonth(v);
              update(v, day, year);
            }}
          >
            <option value=''>—</option>
            {DOB_MONTHS.map((name, idx) => (
              <option key={idx} value={String(idx + 1).padStart(2, '0')}>
                {name}
              </option>
            ))}
          </select>
          <span className={styles.dobLabel}>Month</span>
        </div>
        <div className={styles.dobSelectWrap}>
          <select
            value={day}
            onChange={(e) => {
              const v = e.target.value;
              setDay(v);
              update(month, v, year);
            }}
          >
            <option value=''>—</option>
            {Array.from({ length: 31 }, (_, i) => i + 1).map((d) => (
              <option key={d} value={String(d).padStart(2, '0')}>
                {d}
              </option>
            ))}
          </select>
          <span className={styles.dobLabel}>Day</span>
        </div>
        <div className={styles.dobSelectWrap}>
          <select
            value={year}
            onChange={(e) => {
              const v = e.target.value;
              setYear(v);
              update(month, day, v);
            }}
          >
            <option value=''>—</option>
            {years.map((y) => (
              <option key={y} value={y}>
                {y}
              </option>
            ))}
          </select>
          <span className={styles.dobLabel}>Year</span>
        </div>
      </div>
      {invalid && <span className={styles.err}>Required to sign this consent.</span>}
    </div>
  );
}

function resolveReadonly(value: string, ctx: VariableContext): string {
  if (value === 'today') return today();
  const resolver = VARIABLE_REGISTRY[value];
  if (resolver) return resolver(ctx) ?? '';
  return value;
}

function computeField(compute: 'ageFromDob', fields: FormState, sourceField?: string): string {
  if (compute === 'ageFromDob') {
    const dob = fields[sourceField ?? 'dob'] as string | undefined;
    if (!dob) return '—';
    const ms = Date.now() - new Date(dob).getTime();
    const age = Math.floor(ms / (1000 * 60 * 60 * 24 * 365.25));
    return isNaN(age) || age < 0 ? '—' : String(age);
  }
  return '—';
}

// ── SVG string generators (for PDF rasterization via canvas) ─────────────────

function svgStr(viewBox: string, inner: string): string {
  return `<svg xmlns="http://www.w3.org/2000/svg" viewBox="${viewBox}">${inner}</svg>`;
}

function getEmojiFaceSvgStr(idx: number): string {
  // emojiType='emoji' → golden gradient faces (user's "Emoji" set)
  const [stopA, stopB] = idx <= 3 ? ['#FFE066', '#FFB300'] : ['#FFE580', '#FFA000'];
  const grad = `<defs><radialGradient id="bg" cx="50%" cy="45%" r="55%"><stop offset="0%" stop-color="${stopA}"/><stop offset="100%" stop-color="${stopB}"/></radialGradient></defs>`;
  const face = '<circle cx="80" cy="80" r="72" fill="url(#bg)"/>';
  const eyes: Record<number, string> = {
    0: '<ellipse cx="57" cy="68" rx="9" ry="11" fill="#4A3728"/><ellipse cx="103" cy="68" rx="9" ry="11" fill="#4A3728"/>',
    1: '<ellipse cx="57" cy="70" rx="9" ry="11" fill="#4A3728"/><ellipse cx="103" cy="70" rx="9" ry="11" fill="#4A3728"/>',
    2: '<ellipse cx="57" cy="70" rx="9" ry="11" fill="#4A3728"/><ellipse cx="103" cy="70" rx="9" ry="11" fill="#4A3728"/>',
    3: '<ellipse cx="57" cy="70" rx="9" ry="11" fill="#4A3728"/><ellipse cx="103" cy="70" rx="9" ry="11" fill="#4A3728"/>',
    4: '<path d="M50 72 Q57 61 64 72" stroke="#4A3728" stroke-width="5" fill="none" stroke-linecap="round"/><path d="M96 72 Q103 61 110 72" stroke="#4A3728" stroke-width="5" fill="none" stroke-linecap="round"/>',
  };
  const mouths: Record<number, string> = {
    0: '<path d="M52 108 Q80 88 108 108" stroke="#4A3728" stroke-width="4" fill="none" stroke-linecap="round"/><ellipse cx="112" cy="85" rx="4" ry="7" fill="#64B5F6" opacity="0.85"/>',
    1: '<path d="M55 105 Q80 93 105 105" stroke="#4A3728" stroke-width="4" fill="none" stroke-linecap="round"/>',
    2: '<line x1="55" y1="104" x2="105" y2="104" stroke="#4A3728" stroke-width="4" stroke-linecap="round"/>',
    3: '<path d="M55 97 Q80 116 105 97" stroke="#4A3728" stroke-width="4" fill="none" stroke-linecap="round"/>',
    4: '<path d="M44 94 Q80 138 116 94 Q80 116 44 94 Z" fill="#4A3728"/><path d="M48 96 Q80 126 112 96 Q80 113 48 96 Z" fill="white"/>',
  };
  return svgStr('8 8 144 144', grad + face + (eyes[idx] ?? eyes[0]) + (mouths[idx] ?? mouths[0]));
}

function getColorFaceSvgStr(idx: number): string {
  // emojiType='colors' → simple colored circle faces (user's "Colors" set)
  const fills = ['#66BB6A', '#9CCC65', '#FDD835', '#FF9800', '#E53935'];
  const fill = fills[idx] ?? fills[0];
  const base = `<circle cx="50" cy="50" r="45" fill="${fill}"/><circle cx="37" cy="42" r="4" fill="#1a1a1a"/><circle cx="63" cy="42" r="4" fill="#1a1a1a"/>`;
  const mouths: Record<number, string> = {
    0: '<path d="M33 60 Q50 78 67 60" stroke="#1a1a1a" stroke-width="3.5" fill="none" stroke-linecap="round"/>',
    1: '<path d="M37 63 Q50 72 63 63" stroke="#1a1a1a" stroke-width="3.5" fill="none" stroke-linecap="round"/>',
    2: '<line x1="36" y1="65" x2="64" y2="65" stroke="#1a1a1a" stroke-width="3.5" stroke-linecap="round"/>',
    3: '<path d="M37 70 Q50 59 63 70" stroke="#1a1a1a" stroke-width="3.5" fill="none" stroke-linecap="round"/>',
    4: '<path d="M37 70 Q50 59 63 70" stroke="#1a1a1a" stroke-width="3.5" fill="none" stroke-linecap="round"/><ellipse cx="70" cy="57" rx="3" ry="5" fill="#90CAF9"/>',
  };
  return svgStr('5 5 90 90', base + (mouths[idx] ?? mouths[0]));
}

function getAnimalFaceSvgStr(idx: number): string {
  // idx 0=worst(puppy) … idx 4=best(bunny); preserveAspectRatio aligns face to bottom
  const PA = 'preserveAspectRatio="xMidYMax meet"';
  const svgs: Record<number, [string, string]> = {
    0: [
      '460 462 155 125',
      '<path fill="#B71C1C" d="M475.05,502.274c6.977-18.455,22.496-30.977,34.664-27.967c12.169,3.011,16.378,20.409,9.402,38.864c-6.978,18.456-22.497,30.977-34.665,27.966C472.282,538.129,468.073,520.729,475.05,502.274z"/><path fill="#B71C1C" d="M560.886,512.365c-6.977-18.454-2.768-35.854,9.4-38.864c12.168-3.009,27.688,9.512,34.664,27.967c6.977,18.454,2.768,35.854-9.4,38.864S567.862,530.82,560.886,512.365z"/><circle fill="#E53935" cx="540" cy="525.6" r="54.72"/><ellipse fill="#C62828" cx="540" cy="545.76" rx="23.04" ry="15.84"/><ellipse fill="#C62828" cx="519.15" cy="508.32" rx="19.35" ry="15.84"/><ellipse fill="#333333" cx="540" cy="535.68" rx="10.08" ry="7.2"/><circle fill="#333333" cx="519.84" cy="508.32" r="7.2"/><circle fill="#333333" cx="560.16" cy="508.32" r="7.2"/><path fill="none" stroke="#FFFFFF" stroke-width="3.5" stroke-linecap="round" d="M514.08,560.16c17.28-9.603,34.56-9.603,51.84,0"/><path fill="#64B5F6" d="M567.332,519.307c-5.302,8.193-5.153,19.02,0.26,18.946C573.006,538.179,572.856,527.353,567.332,519.307z"/>',
    ],
    1: [
      '12 6 76 92',
      '<polygon points="22,32 30,8 46,36" fill="#FF9800"/><polygon points="25,30 30,12 42,34" fill="#ffcc80"/><polygon points="54,36 70,8 78,32" fill="#FF9800"/><polygon points="58,34 70,12 75,30" fill="#ffcc80"/><circle cx="50" cy="58" r="38" fill="#FF9800"/><circle cx="37" cy="50" r="5" fill="#333"/><circle cx="63" cy="50" r="5" fill="#333"/><line x1="14" y1="60" x2="34" y2="62" stroke="#333" stroke-width="1.5" stroke-linecap="round" opacity="0.6"/><line x1="14" y1="67" x2="34" y2="65" stroke="#333" stroke-width="1.5" stroke-linecap="round" opacity="0.6"/><line x1="66" y1="62" x2="86" y2="60" stroke="#333" stroke-width="1.5" stroke-linecap="round" opacity="0.6"/><line x1="66" y1="65" x2="86" y2="67" stroke="#333" stroke-width="1.5" stroke-linecap="round" opacity="0.6"/><polygon points="50,63 47,68 53,68" fill="#e64a19"/><path d="M 32 78 Q 50 68 68 78" stroke="#333" stroke-width="3.5" fill="none" stroke-linecap="round"/>',
    ],
    2: [
      '10 -5 80 105',
      '<line x1="40" y1="24" x2="32" y2="4" stroke="#FDD835" stroke-width="3" stroke-linecap="round"/><circle cx="32" cy="2" r="5" fill="#FDD835" stroke="#c8a800" stroke-width="1.5"/><line x1="60" y1="24" x2="68" y2="4" stroke="#FDD835" stroke-width="3" stroke-linecap="round"/><circle cx="68" cy="2" r="5" fill="#FDD835" stroke="#c8a800" stroke-width="1.5"/><circle cx="50" cy="60" r="38" fill="#FDD835"/><ellipse cx="37" cy="54" rx="7" ry="9" fill="#333"/><ellipse cx="63" cy="54" rx="7" ry="9" fill="#333"/><ellipse cx="35" cy="52" rx="2.5" ry="3" fill="#fff" opacity="0.4"/><ellipse cx="61" cy="52" rx="2.5" ry="3" fill="#fff" opacity="0.4"/><line x1="36" y1="70" x2="64" y2="70" stroke="#333" stroke-width="3" stroke-linecap="round"/>',
    ],
    3: [
      '10 12 80 88',
      '<circle cx="28" cy="28" r="14" fill="#9CCC65"/><circle cx="72" cy="28" r="14" fill="#9CCC65"/><circle cx="28" cy="28" r="8" fill="#c5e1a5"/><circle cx="72" cy="28" r="8" fill="#c5e1a5"/><circle cx="50" cy="58" r="38" fill="#9CCC65"/><ellipse cx="50" cy="68" rx="16" ry="11" fill="#c5e1a5"/><circle cx="37" cy="50" r="5" fill="#333"/><circle cx="63" cy="50" r="5" fill="#333"/><path d="M 40 70 Q 50 78 60 70" stroke="#333" stroke-width="3" fill="none" stroke-linecap="round"/>',
    ],
    4: [
      '10 -2 80 102',
      '<ellipse cx="36" cy="18" rx="8" ry="18" fill="#66BB6A"/><ellipse cx="36" cy="18" rx="4" ry="13" fill="#a5d6a7"/><ellipse cx="64" cy="18" rx="8" ry="18" fill="#66BB6A"/><ellipse cx="64" cy="18" rx="4" ry="13" fill="#a5d6a7"/><circle cx="50" cy="60" r="38" fill="#66BB6A"/><circle cx="37" cy="52" r="5" fill="#333"/><circle cx="63" cy="52" r="5" fill="#333"/><path d="M 30 66 Q 50 86 70 66" stroke="#333" stroke-width="3.5" fill="none" stroke-linecap="round"/><circle cx="28" cy="64" r="7" fill="#ff8a80" opacity="0.5"/><circle cx="72" cy="64" r="7" fill="#ff8a80" opacity="0.5"/><ellipse cx="50" cy="59" rx="3" ry="2" fill="#ff8a80"/>',
    ],
  };
  const [vb, inner] = svgs[idx] ?? svgs[0];
  return `<svg xmlns="http://www.w3.org/2000/svg" viewBox="${vb}" ${PA}>${inner}</svg>`;
}

function getCloudFaceSvgStr(idx: number): string {
  if (idx === 4) {
    const rays = [0, 45, 90, 135, 180, 225, 270, 315]
      .map((deg) => {
        const r = (deg * Math.PI) / 180;
        const x1 = (80 + Math.cos(r) * 35).toFixed(1);
        const y1 = (80 + Math.sin(r) * 35).toFixed(1);
        const x2 = (80 + Math.cos(r) * 48).toFixed(1);
        const y2 = (80 + Math.sin(r) * 48).toFixed(1);
        return `<line x1="${x1}" y1="${y1}" x2="${x2}" y2="${y2}" stroke="#FFD700" stroke-width="3" stroke-linecap="round"/>`;
      })
      .join('');
    return svgStr(
      '15 15 130 130',
      `<circle cx="80" cy="80" r="28" fill="#FFD700" opacity="0.3"/>${rays}<circle cx="80" cy="80" r="25" fill="#FFD700"/><circle cx="70" cy="72" r="3" fill="#333"/><circle cx="90" cy="72" r="3" fill="#333"/><path d="M 68 84 Q 80 94 92 84" stroke="#333" stroke-width="2.5" fill="none" stroke-linecap="round"/>`,
    );
  }
  const cloudFills = ['#3D3D4D', '#6B6B7B', '#B0B0C0', '#E8E8F0'];
  const strokes = ['#D0D0D0', '#D0D0D0', '#333', '#333'];
  const fill = cloudFills[idx] ?? cloudFills[0];
  const stroke = strokes[idx] ?? strokes[0];
  const cloudBase = `<ellipse cx="65" cy="80" rx="12" ry="14" fill="${fill}"/><ellipse cx="80" cy="75" rx="16" ry="16" fill="${fill}"/><ellipse cx="95" cy="80" rx="12" ry="14" fill="${fill}"/><rect x="56" y="80" width="48" height="12" fill="${fill}"/>`;
  const faces: Record<number, string> = {
    0: `<path d="M 68 77 Q 72 79 76 77" stroke="${stroke}" stroke-width="2.5" fill="none" stroke-linecap="round"/><path d="M 84 77 Q 88 79 92 77" stroke="${stroke}" stroke-width="2.5" fill="none" stroke-linecap="round"/><path d="M 70 86 Q 80 81 90 86" stroke="${stroke}" stroke-width="3" fill="none" stroke-linecap="round"/>`,
    1: `<path d="M 68 77 Q 72 79 76 77" stroke="${stroke}" stroke-width="2.5" fill="none" stroke-linecap="round"/><path d="M 84 77 Q 88 79 92 77" stroke="${stroke}" stroke-width="2.5" fill="none" stroke-linecap="round"/><path d="M 70 86 Q 80 81 90 86" stroke="${stroke}" stroke-width="3" fill="none" stroke-linecap="round"/>`,
    2: '<circle cx="72" cy="78" r="3" fill="#E0E0E0"/><circle cx="88" cy="78" r="3" fill="#E0E0E0"/><circle cx="72" cy="78" r="1.5" fill="#333"/><circle cx="88" cy="78" r="1.5" fill="#333"/><path d="M 74 84 Q 80 82 86 84" stroke="#333" stroke-width="2" fill="none" stroke-linecap="round"/>',
    3: '<circle cx="72" cy="78" r="3" fill="#E0E0E0"/><circle cx="88" cy="78" r="3" fill="#E0E0E0"/><circle cx="72" cy="78" r="1.5" fill="#333"/><circle cx="88" cy="78" r="1.5" fill="#333"/><path d="M 74 83 Q 80 86 86 83" stroke="#333" stroke-width="2" fill="none" stroke-linecap="round"/>',
  };
  const rain: Record<number, string> = {
    0:
      [72, 92]
        .map(
          (x) =>
            `<line x1="${x}" y1="95" x2="${x}" y2="125" stroke="#4A90E2" stroke-width="3" stroke-linecap="round"/>`,
        )
        .join('') +
      '<path d="M 80 95 L 84 102 L 80 110 L 84 118" stroke="#FFD700" stroke-width="2.5" fill="none" stroke-linecap="round" stroke-linejoin="round"/>',
    1: [72, 92]
      .map(
        (x) => `<line x1="${x}" y1="95" x2="${x}" y2="125" stroke="#4A90E2" stroke-width="3" stroke-linecap="round"/>`,
      )
      .join(''),
    2: [60, 70, 80, 90, 100]
      .map(
        (x) =>
          `<line x1="${x}" y1="95" x2="${x}" y2="125" stroke="#4A90E2" stroke-width="2.5" stroke-linecap="round"/>`,
      )
      .join(''),
    3: [65, 80, 95]
      .map(
        (x) =>
          `<line x1="${x}" y1="95" x2="${x}" y2="125" stroke="#4A90E2" stroke-width="2.5" stroke-linecap="round"/>`,
      )
      .join(''),
  };
  return svgStr('44 55 72 80', cloudBase + (faces[idx] ?? '') + (rain[idx] ?? ''));
}

function getShapeFaceSvgStr(idx: number): string {
  const shapes: Record<number, string> = {
    0: '<path fill="#E57373" d="M180.966,81.204l-24.578-43.375c-6.748-11.951-16.387-17.928-28.916-17.928H72.528c-12.529,0-22.168,5.977-28.917,17.928L19.034,81.204c-7.711,12.531-7.711,25.062,0,37.592l24.578,43.375c6.749,11.953,16.388,17.928,28.917,17.928h54.943c12.529,0,22.168-5.975,28.916-17.928l24.578-43.375C188.678,106.266,188.678,93.735,180.966,81.204z"/><ellipse fill="#0A0A0A" cx="75" cy="85" rx="4.5" ry="7"/><ellipse fill="#0A0A0A" cx="125" cy="85" rx="4.5" ry="7"/><path fill="none" stroke="#0A0A0A" stroke-width="6" stroke-linecap="round" d="M75,135c16.667-16.666,33.334-16.666,50,0"/><path fill="#64B5F6" d="M129.871,98.382c-4.757,15.475,2.216,33.11,11.621,29.391C150.897,124.055,143.925,106.42,129.871,98.382z"/>',
    1: '<rect x="20" y="20" width="160" height="160" rx="32" ry="32" fill="#FF8A3D"/><ellipse cx="75" cy="85" rx="4.5" ry="7" fill="#0a0a0a"/><ellipse cx="125" cy="85" rx="4.5" ry="7" fill="#0a0a0a"/><path d="M75 135 Q 100 110, 125 135" fill="none" stroke="#0a0a0a" stroke-width="6" stroke-linecap="round"/>',
    2: '<circle cx="100" cy="100" r="80" fill="#FFD42A"/><ellipse cx="75" cy="88" rx="4.5" ry="7" fill="#0a0a0a"/><ellipse cx="125" cy="88" rx="4.5" ry="7" fill="#0a0a0a"/><line x1="75" y1="125" x2="125" y2="125" stroke="#0a0a0a" stroke-width="6" stroke-linecap="round"/>',
    3: '<path d="M100 18 L123 78 L188 82 L137 122 L155 184 L100 148 L45 184 L63 122 L12 82 L77 78 Z" fill="#5BD5C9"/><path d="M68 92 Q 78 80, 88 92" fill="none" stroke="#0a0a0a" stroke-width="5" stroke-linecap="round"/><path d="M112 92 Q 122 80, 132 92" fill="none" stroke="#0a0a0a" stroke-width="5" stroke-linecap="round"/><path d="M72 115 Q 100 145, 128 115" fill="none" stroke="#0a0a0a" stroke-width="6" stroke-linecap="round"/>',
    4: '<path d="M100 175 C 60 145, 20 115, 20 75 C 20 50, 40 30, 65 30 C 82 30, 95 40, 100 55 C 105 40, 118 30, 135 30 C 160 30, 180 50, 180 75 C 180 115, 140 145, 100 175 Z" fill="#3FBF5A"/><path d="M55 78 Q 65 65, 75 78" fill="none" stroke="#0a0a0a" stroke-width="6" stroke-linecap="round"/><path d="M125 78 Q 135 65, 145 78" fill="none" stroke="#0a0a0a" stroke-width="6" stroke-linecap="round"/><path d="M70 100 Q 100 145, 130 100 Q 100 115, 70 100 Z" fill="#0a0a0a"/>',
  };
  return svgStr('0 0 200 200', shapes[idx] ?? shapes[0]);
}

function getEmojiSvgString(emojiType: string, idx: number): string {
  switch (emojiType) {
    case 'colors':
      return getColorFaceSvgStr(idx);
    case 'animals':
      return getAnimalFaceSvgStr(idx);
    case 'clouds':
      return getCloudFaceSvgStr(idx);
    case 'shapes':
      return getShapeFaceSvgStr(idx);
    default:
      return getEmojiFaceSvgStr(idx); // 'emoji' = golden gradient
  }
}

function svgStringToDataUrl(svgString: string, size = 120): Promise<string> {
  return new Promise((resolve) => {
    const blob = new Blob([svgString], { type: 'image/svg+xml;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const img = new Image();
    img.onload = () => {
      const canvas = document.createElement('canvas');
      canvas.width = size;
      canvas.height = size;
      const ctx = canvas.getContext('2d');
      if (ctx) ctx.drawImage(img, 0, 0, size, size);
      URL.revokeObjectURL(url);
      resolve(canvas.toDataURL('image/png'));
    };
    img.onerror = () => {
      URL.revokeObjectURL(url);
      resolve('');
    };
    img.src = url;
  });
}

function toRoman(n: number): string {
  const vals = [1000, 900, 500, 400, 100, 90, 50, 40, 10, 9, 5, 4, 1];
  const syms = ['m', 'cm', 'd', 'cd', 'c', 'xc', 'l', 'xl', 'x', 'ix', 'v', 'iv', 'i'];
  let result = '';
  for (let i = 0; i < vals.length; i++) {
    while (n >= vals[i]) {
      result += syms[i];
      n -= vals[i];
    }
  }
  return result;
}

function urlToDataUrl(src: string): Promise<string> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.crossOrigin = 'anonymous';
    img.onload = () => {
      const canvas = document.createElement('canvas');
      canvas.width = img.naturalWidth;
      canvas.height = img.naturalHeight;
      const ctx = canvas.getContext('2d');
      if (ctx) ctx.drawImage(img, 0, 0);
      resolve(canvas.toDataURL('image/png'));
    };
    img.onerror = () => reject(new Error(`Failed to load image: ${src}`));
    img.src = src;
  });
}

export function ConsentFormRenderer({
  definition,
  providerName,
  providerFirstName,
  providerLastName,
  providerTitle,
  providerSuffix,
  providerSpeciality,
  providerRoomURL,
  providerEmail,
  organizationName,
  clientLabel = 'Patient',
  preview = false,
  isBlankPreview = false,
  builderConfig,
  isHost = false,
  mode = 'selfpaced',
  onModeChange,
  onScroll,
  hostScrollTarget,
  highlightedBlockId,
  onHighlightBlock,
  participantName,
  onSubmit,
  onClose,
  onDownload,
  onFieldChange,
  onApplySignature,
  onGoBack,
  goBackSignal,
  externalFieldState,
  externalSignature,
  receipt,
  clientDownloaded = false,
  clientViewport,
  clientEndedSession = false,
  onDismissClientEnded,
}: Props) {
  const applyLabel = (text: string): string => {
    const lower = clientLabel.toLowerCase();
    const upper = clientLabel.toUpperCase();
    const title = clientLabel.charAt(0).toUpperCase() + clientLabel.slice(1);
    return text.replace(/\b(patient|client)\b/gi, (match) => {
      if (match === match.toUpperCase()) return upper;
      if (match[0] === match[0].toUpperCase()) return title;
      return lower;
    });
  };

  const resolveText = (text: string, ctx: VariableContext): string => applyLabel(injectVariables(text, ctx));

  const renderInlineText = (text: string): React.ReactNode => {
    const resolved = resolveText(text, varCtx);
    const linkPattern = /\[([^\]]+)\]\((https?:\/\/[^)]+)\)/g;
    const parts: React.ReactNode[] = [];
    let lastIndex = 0;
    let match;
    let key = 0;
    while ((match = linkPattern.exec(resolved)) !== null) {
      if (match.index > lastIndex) parts.push(resolved.slice(lastIndex, match.index));
      parts.push(
        <a key={key++} href={match[2]} target='_blank' rel='noopener noreferrer'>
          {match[1]}
        </a>,
      );
      lastIndex = match.index + match[0].length;
    }
    if (lastIndex < resolved.length) {
      // Remove a stray space that ends up immediately before punctuation after a link
      // (e.g. "[link](url) ." → "[link](url).").
      const after = resolved.slice(lastIndex).replace(/^ (?=[.,;:!?])/, '');
      if (after) parts.push(after);
    }
    return parts.length === 0 ? resolved : parts.length === 1 ? parts[0] : <>{parts}</>;
  };

  const renderIntro = (intro: string) => {
    const name = providerName?.trim() || (fields['__providerName'] as string)?.trim() || 'your provider';
    const resolved = injectVariables(intro, { ...varCtx, providerName: name });
    const parts = resolved.split(name);
    if (parts.length <= 1) return <>{applyLabel(resolved)}</>;
    return (
      <>
        {applyLabel(parts[0])}
        <strong>{name}</strong>
        {applyLabel(parts.slice(1).join(name))}
      </>
    );
  };

  const [clientViewMode, setClientViewMode] = useState(
    () => localStorage.getItem('teleconsentHostClientView') !== 'false',
  );

  const toggleClientView = () => {
    const next = !clientViewMode;
    setClientViewMode(next);
    localStorage.setItem('teleconsentHostClientView', String(next));
  };

  const [localFields, setLocalFields] = useState<FormState>({ relationship: 'self' });
  const fields = externalFieldState ?? localFields;

  const varCtx: VariableContext = {
    providerName: providerName ?? (fields['__providerName'] as string) ?? undefined,
    providerFirstName: providerFirstName ?? (fields['__providerFirstName'] as string) ?? undefined,
    providerLastName: providerLastName ?? (fields['__providerLastName'] as string) ?? undefined,
    providerTitle: providerTitle ?? (fields['__providerTitle'] as string) ?? undefined,
    providerSuffix: providerSuffix ?? (fields['__providerSuffix'] as string) ?? undefined,
    providerSpeciality: providerSpeciality ?? (fields['__providerSpeciality'] as string) ?? undefined,
    providerRoomURL: providerRoomURL ?? (fields['__providerRoomURL'] as string) ?? undefined,
    providerEmail: providerEmail ?? (fields['__providerEmail'] as string) ?? undefined,
    organizationName: organizationName ?? (fields['__organizationName'] as string) ?? undefined,
    clientLabel,
  };

  const [sigMode, setSigMode] = useState<'draw' | 'type'>('draw');
  const [localSig, setLocalSig] = useState<SignatureState>({
    kind: 'draw',
    dataUrl: '',
    text: '',
  });
  const signature = externalSignature ?? localSig;

  // Keep host's signature tab in sync with client's chosen mode
  useEffect(() => {
    if (isHost && externalSignature?.kind) {
      setSigMode(externalSignature.kind);
    }
  }, [isHost, externalSignature?.kind]);

  const [signed, setSigned] = useState(false);
  const [sessionInitials, setSessionInitials] = useState<SessionInitials | null>(null);

  const [showErr, setShowErr] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [downloadingPdf, setDownloadingPdf] = useState(false);
  const [downloadingBlank, setDownloadingBlank] = useState(false);
  const [localReceipt, setLocalReceipt] = useState<SubmitReceipt | null>(null);
  const [confirmClose, setConfirmClose] = useState(false);
  const [snackbar, setSnackbar] = useState<string | null>(null);

  const finalReceipt = receipt ?? localReceipt;

  const bodyRef = useRef<HTMLDivElement>(null);
  const titleRef = useRef<HTMLHeadingElement>(null);
  const navRef = useRef<HTMLElement>(null);
  const navBtnRefs = useRef<Record<string, HTMLButtonElement | null>>({});
  const sectionRefs = useRef<Record<string, HTMLElement | null>>({});
  const [activeSection, setActiveSection] = useState(definition.sections[0]?.id ?? '');
  const [visitedSections, setVisitedSections] = useState<Set<string>>(
    () => new Set(definition.sections[0]?.id ? [definition.sections[0].id] : []),
  );
  const [navCanScrollLeft, setNavCanScrollLeft] = useState(false);
  const [navCanScrollRight, setNavCanScrollRight] = useState(false);

  // Builder nav label inline editing state
  const [editingNavId, setEditingNavId] = useState<string | null>(null);
  const [editingNavLabel, setEditingNavLabel] = useState('');

  // Builder block drag-to-reorder state
  const [blockDragKey, setBlockDragKey] = useState<string | null>(null);
  const [blockDragOverKey, setBlockDragOverKey] = useState<string | null>(null);
  const [blockDragOverHalf, setBlockDragOverHalf] = useState<'top' | 'bottom' | 'left' | 'right'>('top');
  const blockDragRef = useRef<{ sectionId: string; blockIndex: number; halfWidth: boolean } | null>(null);

  // Global dragend cleanup: clears block hover outlines whenever any drag ends,
  // including palette drags that drop on a DropZone instead of a block.
  useEffect(() => {
    if (!builderConfig) return;
    const clearDrag = () => {
      blockDragRef.current = null;
      setBlockDragKey(null);
      setBlockDragOverKey(null);
    };
    document.addEventListener('dragend', clearDrag);
    return () => document.removeEventListener('dragend', clearDrag);
  }, [builderConfig]);

  const setField = useCallback(
    (id: string, value: string | boolean) => {
      if (externalFieldState) {
        onFieldChange?.(id, value);
      } else {
        setLocalFields((prev) => ({ ...prev, [id]: value }));
        onFieldChange?.(id, value);
      }
    },
    [externalFieldState, onFieldChange],
  );

  const handleCommit = (commit: SignatureCommit) => {
    const next: SignatureState = {
      kind: commit.kind,
      dataUrl: commit.kind === 'draw' ? commit.dataUrl : '',
      text: commit.kind === 'type' ? commit.text : '',
    };
    if (!externalSignature) setLocalSig(next);
    onFieldChange?.('signature', next.dataUrl || next.text);
  };

  // ---- progress ----
  const isFieldFilled = (id: string): boolean => {
    if (id === 'signature') return isHost ? !!(externalSignature?.dataUrl || externalSignature?.text) : signed;
    if (id.startsWith('__ck:')) {
      const blockId = id.slice(5);
      for (const sec of definition.sections) {
        for (const blk of sec.blocks) {
          if (blk.type === 'checklist' && blk.id === blockId) {
            return blk.options.some((o) => Boolean(fields[`${blockId}:${o.id}`]));
          }
        }
      }
      return false;
    }
    if (id.startsWith('__sc:')) return Boolean(fields[id.slice(5)]);
    if (id.startsWith('__mx:')) return Boolean(fields[id.slice(5)]);
    if (id.startsWith('__ip:')) return Boolean(fields[id]);
    if (id.startsWith('__addr:')) {
      const rest = id.slice(7);
      const sep = rest.indexOf(':');
      const blockId = rest.slice(0, sep);
      const sfKey = rest.slice(sep + 1);
      const v = fields[`${blockId}:${sfKey}`];
      return typeof v === 'string' ? v.trim().length > 0 : Boolean(v);
    }
    if (id.startsWith('__tf:')) return Boolean(fields[id.slice(5)]);
    if (id.startsWith('__pn:')) {
      const rest = id.slice(5);
      const sep = rest.indexOf(':');
      const blockId = rest.slice(0, sep);
      const fieldKey = rest.slice(sep + 1);
      const v = fields[`${blockId}:${fieldKey}`];
      return typeof v === 'string' ? v.trim().length > 0 : Boolean(v);
    }
    const v = fields[id];
    if (typeof v !== 'string') return Boolean(v);
    const trimmed = v.trim();
    if (!trimmed) return false;
    // Date fields encode as "yyyy-mm-dd" — all three parts must be non-empty
    const parts = trimmed.split('-');
    if (parts.length === 3) return parts.every(Boolean);
    return true;
  };

  // Collect all required items in strict document order (section → block → field)
  const reqSet = new Set(definition.requiredFields);
  const allOrderedItems: { id: string; label: string }[] = [];
  const seenIds = new Set<string>();

  const addItem = (id: string, label: string) => {
    if (!seenIds.has(id)) {
      seenIds.add(id);
      allOrderedItems.push({ id, label });
    }
  };

  for (const sec of definition.sections) {
    for (const blk of sec.blocks) {
      if (blk.type === 'field-group') {
        for (const f of blk.fields) {
          if (reqSet.has(f.id)) {
            addItem(f.id, applyLabel(f.label) || f.id);
          }
        }
      } else if (blk.type === 'checklist' && blk.required) {
        const fallback = blk.variant === 'radio' ? 'Radio' : 'Checkbox';
        addItem(`__ck:${blk.id}`, (blk.label ?? '').trim() || fallback);
      } else if (blk.type === 'scale' && blk.required) {
        addItem(`__sc:${blk.id}`, blk.label.trim() || 'Scale');
      } else if (blk.type === 'matrix' && blk.required) {
        blk.rows.forEach((row) => addItem(`__mx:${blk.id}:${row.id}`, row.text.trim() || 'Matrix row'));
      } else if (blk.type === 'initials-pad' && blk.required) {
        blk.rows.forEach((row) => addItem(`__ip:${row.id}`, 'Initials'));
      } else if (blk.type === 'address') {
        blk.subfields
          .filter((sf) => sf.enabled && sf.required && sf.fieldType !== 'select-country')
          .forEach((sf) => addItem(`__addr:${blk.id}:${sf.key}`, sf.label.trim() || 'Address'));
      } else if (blk.type === 'time-field' && blk.required) {
        addItem(`__tf:${blk.id}`, blk.label.trim() || 'Time');
      } else if (blk.type === 'print-name') {
        if (blk.nameRequired) addItem(`__pn:${blk.id}:name`, 'Your Name');
        if (blk.signingAsRequired) addItem(`__pn:${blk.id}:signingAs`, 'Signing As');
      } else if (blk.type === 'signature' && blk.required !== false) {
        addItem('signature', 'Signature Pad');
      }
    }
  }

  // Catch any definition.requiredFields IDs not encountered in the block scan
  for (const id of definition.requiredFields) {
    if (!seenIds.has(id)) {
      addItem(id, id === 'signature' ? 'Signature Pad' : id);
    }
  }

  const requiredCount = allOrderedItems.length;
  const filledCount = allOrderedItems.filter(({ id }) => isFieldFilled(id)).length;

  // Pill excludes 'relationship' — visible default selection so it always feels pre-filled
  const pillItems = allOrderedItems.filter(({ id }) => id !== 'relationship');
  const pillRequiredCount = pillItems.length;
  const pillFilledCount = pillItems.filter(({ id }) => isFieldFilled(id)).length;
  const remainingItems = pillItems.filter(({ id }) => !isFieldFilled(id));
  // ---- section completion ----
  const sectionDone = (section: ConsentSection): boolean => {
    if (!visitedSections.has(section.id)) return false;
    const fieldIds = section.blocks
      .filter((b) => b.type === 'field-group')
      .flatMap((b) => (b as { type: 'field-group'; fields: FormField[] }).fields.map((f) => f.id));
    const sigBlock = section.blocks.some((b) => b.type === 'signature');
    const requiredFieldsDone = definition.requiredFields
      .filter((id) => (id === 'signature' ? sigBlock : fieldIds.includes(id)))
      .every(isFieldFilled);
    return requiredFieldsDone;
  };

  // ---- scroll spy ----
  useEffect(() => {
    const root = bodyRef.current;
    if (!root) return;
    const handleScroll = () => {
      const top = root.scrollTop + 24;
      let cur = definition.sections[0]?.id ?? '';
      for (const s of definition.sections) {
        const el = sectionRefs.current[s.id];
        if (!el) continue;
        const elTop = el.getBoundingClientRect().top - root.getBoundingClientRect().top + root.scrollTop;
        if (elTop <= top) cur = s.id;
      }
      setActiveSection(cur);
      setVisitedSections((prev) => (prev.has(cur) ? prev : new Set([...prev, cur])));
    };
    root.addEventListener('scroll', handleScroll, { passive: true });
    handleScroll();
    return () => root.removeEventListener('scroll', handleScroll);
  }, [definition.sections]);

  // ---- secNav: auto-scroll active button into view (mobile) ----
  useEffect(() => {
    const nav = navRef.current;
    const btn = navBtnRefs.current[activeSection];
    if (!nav || !btn) return;
    const navLeft = nav.scrollLeft;
    const navRight = navLeft + nav.clientWidth;
    const btnLeft = btn.offsetLeft;
    const btnRight = btnLeft + btn.offsetWidth;
    if (btnLeft < navLeft + 8) {
      nav.scrollTo({ left: Math.max(0, btnLeft - 12), behavior: 'smooth' });
    } else if (btnRight > navRight - 8) {
      nav.scrollTo({ left: btnRight - nav.clientWidth + 12, behavior: 'smooth' });
    }
  }, [activeSection]);

  // ---- secNav: wheel → horizontal scroll + click-drag ----
  useEffect(() => {
    const nav = navRef.current;
    if (!nav) return;

    const onWheel = (e: WheelEvent) => {
      if (Math.abs(e.deltaX) > Math.abs(e.deltaY)) return; // already horizontal
      e.preventDefault();
      nav.scrollLeft += e.deltaY;
    };

    let dragging = false;
    let moved = false;
    let startX = 0;
    let startScroll = 0;

    const onMouseDown = (e: MouseEvent) => {
      dragging = true;
      moved = false;
      startX = e.clientX;
      startScroll = nav.scrollLeft;
      nav.style.userSelect = 'none';
    };

    const onMouseMove = (e: MouseEvent) => {
      if (!dragging) return;
      const dx = e.clientX - startX;
      if (!moved && Math.abs(dx) > 4) {
        moved = true;
        nav.style.cursor = 'grabbing';
      }
      if (moved) nav.scrollLeft = startScroll - dx;
    };

    const onMouseUp = () => {
      dragging = false;
      nav.style.cursor = '';
      nav.style.userSelect = '';
    };

    // Suppress click on buttons when the pointer actually dragged
    const onCapureClick = (e: MouseEvent) => {
      if (moved) e.stopPropagation();
    };

    nav.addEventListener('wheel', onWheel, { passive: false });
    nav.addEventListener('mousedown', onMouseDown);
    nav.addEventListener('click', onCapureClick, { capture: true });
    window.addEventListener('mousemove', onMouseMove);
    window.addEventListener('mouseup', onMouseUp);

    return () => {
      nav.removeEventListener('wheel', onWheel);
      nav.removeEventListener('mousedown', onMouseDown);
      nav.removeEventListener('click', onCapureClick, { capture: true });
      window.removeEventListener('mousemove', onMouseMove);
      window.removeEventListener('mouseup', onMouseUp);
    };
  }, []);

  // ---- secNav: track whether left/right scroll arrows should be visible ----
  useEffect(() => {
    const nav = navRef.current;
    if (!nav) return;
    const update = () => {
      setNavCanScrollLeft(nav.scrollLeft > 1);
      setNavCanScrollRight(nav.scrollLeft + nav.clientWidth < nav.scrollWidth - 1);
    };
    update();
    nav.addEventListener('scroll', update, { passive: true });
    const ro = new ResizeObserver(update);
    ro.observe(nav);
    return () => {
      nav.removeEventListener('scroll', update);
      ro.disconnect();
    };
  }, []);

  // ---- shrink title font until it fits on one line ----
  useEffect(() => {
    const el = titleRef.current;
    if (!el) return;
    const fit = () => {
      el.style.fontSize = '';
      const maxSize = 22;
      let size = maxSize;
      el.style.fontSize = `${size}px`;
      while (el.scrollWidth > el.offsetWidth && size > 12) {
        size -= 0.5;
        el.style.fontSize = `${size}px`;
      }
    };
    fit();
    const ro = new ResizeObserver(fit);
    ro.observe(el);
    return () => ro.disconnect();
  }, [definition.title]);

  // ---- lock page scroll behind the modal (prevents EditWaitingRoom bg scroll) ----
  useEffect(() => {
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = prev;
    };
  }, []);

  // ---- both sides: show done overlay as soon as receipt arrives ----
  useEffect(() => {
    if (receipt && !submitted) setSubmitted(true);
  }, [receipt]);

  // ---- client: host signalled to go back to form view ----
  useEffect(() => {
    if (goBackSignal && !isHost) setSubmitted(false);
  }, [goBackSignal]);

  // ---- snap to current position when entering guided (host→client) or follow (client→host) mode ----
  useEffect(() => {
    if (!onScroll || mode === 'selfpaced') return;
    if (mode === 'guided' && !isHost) return;
    if (mode === 'follow' && isHost) return;
    const root = bodyRef.current;
    if (!root) return;
    const rootTop = root.getBoundingClientRect().top;
    const scrollTop = root.scrollTop + 24;
    let activeSectionId = definition.sections[0]?.id ?? '';
    for (const s of definition.sections) {
      const el = sectionRefs.current[s.id];
      if (!el) continue;
      const elTop = el.getBoundingClientRect().top - rootTop + root.scrollTop;
      if (elTop <= scrollTop) activeSectionId = s.id;
    }
    const el = sectionRefs.current[activeSectionId];
    if (!el) return;
    const elTop = el.getBoundingClientRect().top - rootTop + root.scrollTop;
    const fraction = Math.max(0, Math.min(1, (root.scrollTop - elTop) / Math.max(el.offsetHeight, 1)));
    onScroll(activeSectionId, fraction);
  }, [mode]); // intentionally only re-runs when mode changes

  // ---- emit scroll position: host→client in guided, client→host in follow ----
  useEffect(() => {
    const root = bodyRef.current;
    if (!root || !onScroll) return;
    const handler = () => {
      if (!((mode === 'guided' && isHost) || (mode === 'follow' && !isHost))) return;
      const rootTop = root.getBoundingClientRect().top;
      const scrollTop = root.scrollTop + 24;
      let activeSectionId = definition.sections[0]?.id ?? '';
      for (const s of definition.sections) {
        const el = sectionRefs.current[s.id];
        if (!el) continue;
        const elTop = el.getBoundingClientRect().top - rootTop + root.scrollTop;
        if (elTop <= scrollTop) activeSectionId = s.id;
      }
      const el = sectionRefs.current[activeSectionId];
      if (!el) return;
      const elTop = el.getBoundingClientRect().top - rootTop + root.scrollTop;
      const fraction = Math.max(0, Math.min(1, (root.scrollTop - elTop) / Math.max(el.offsetHeight, 1)));
      onScroll(activeSectionId, fraction);
    };
    root.addEventListener('scroll', handler, { passive: true });
    return () => root.removeEventListener('scroll', handler);
  }, [mode, onScroll, definition.sections]);

  // ---- lock scroll: client in guided, host in follow ----
  useEffect(() => {
    const root = bodyRef.current;
    if (!root) return;
    if (!((mode === 'guided' && !isHost) || (mode === 'follow' && isHost))) return;
    const prevent = (e: Event) => e.preventDefault();
    root.addEventListener('wheel', prevent, { passive: false });
    root.addEventListener('touchmove', prevent, { passive: false });
    return () => {
      root.removeEventListener('wheel', prevent);
      root.removeEventListener('touchmove', prevent);
    };
  }, [mode, isHost]);

  useEffect(() => {
    if (!hostScrollTarget || !((mode === 'guided' && !isHost) || (mode === 'follow' && isHost))) return;
    const root = bodyRef.current;
    if (!root) return;
    if (hostScrollTarget.sectionId === definition.sections[0]?.id && hostScrollTarget.sectionFraction === 0) {
      root.scrollTo({ top: 0, behavior: 'smooth' });
      return;
    }
    const el = sectionRefs.current[hostScrollTarget.sectionId];
    if (!el) return;
    const rootTop = root.getBoundingClientRect().top;
    const elTop = el.getBoundingClientRect().top - rootTop + root.scrollTop;
    const target = elTop + el.offsetHeight * hostScrollTarget.sectionFraction;
    root.scrollTo({ top: Math.max(0, target), behavior: 'smooth' });
  }, [hostScrollTarget, mode, isHost]);

  const scrollNav = (dir: 'left' | 'right') => {
    const nav = navRef.current;
    if (!nav) return;
    nav.scrollBy({ left: dir === 'right' ? nav.clientWidth * 0.7 : -(nav.clientWidth * 0.7), behavior: 'smooth' });
  };

  const jumpTo = (id: string) => {
    const root = bodyRef.current;
    if (!root) return;
    // First section always scrolls to top so the intro pill is visible
    if (id === definition.sections[0]?.id) {
      root.scrollTo({ top: 0, behavior: 'smooth' });
      return;
    }
    const el = sectionRefs.current[id];
    if (!el) return;
    const elTop = el.getBoundingClientRect().top - root.getBoundingClientRect().top + root.scrollTop;
    root.scrollTo({ top: Math.max(0, elTop + 2), behavior: 'smooth' });
  };

  // ---- snackbar auto-dismiss ----
  useEffect(() => {
    if (!snackbar) return;
    const t = window.setTimeout(() => setSnackbar(null), 3000);
    return () => window.clearTimeout(t);
  }, [snackbar]);

  const MODE_SNACKBAR: Record<TeleconsentMode, string> = {
    guided: 'Guided mode enabled: Host controls scrolling.',
    follow: 'Follow mode enabled: Client controls scrolling.',
    selfpaced: 'Self-paced mode enabled: Independent scrolling.',
  };

  const handleModeChange = (newMode: TeleconsentMode) => {
    onModeChange?.(newMode);
    setSnackbar(MODE_SNACKBAR[newMode]);
  };

  // True when this side's scrolling is locked: client in guided, host in follow
  const scrollLocked = (!isHost && mode === 'guided') || (isHost && mode === 'follow');

  const trySubmit = async () => {
    if (filledCount < requiredCount) {
      setShowErr(true);
      const firstIncomplete = definition.sections.find((s) => !sectionDone(s));
      if (firstIncomplete) jumpTo(firstIncomplete.id);
      return;
    }
    setSubmitting(true);
    try {
      if (onSubmit) {
        const r = await onSubmit();
        setLocalReceipt(r);
      } else {
        setLocalReceipt({
          submittedAt: new Date().toISOString(),
          clientIp: '—',
        });
      }
      setSubmitted(true);
    } finally {
      setSubmitting(false);
    }
  };

  const buildPdf = async (blank = false) => {
    // Pre-rasterize emoji SVGs for all scale blocks in this form
    const emojiPngCache: Record<string, Record<number, string>> = {};
    for (const section of definition.sections) {
      for (const block of section.blocks) {
        if (block.type === 'scale' && (block as { scaleType?: string }).scaleType === 'emoji') {
          const et = (block as { emojiType?: string }).emojiType ?? 'emoji';
          if (!emojiPngCache[et]) {
            emojiPngCache[et] = {};
            for (let fi = 0; fi < 5; fi++) {
              emojiPngCache[et][fi] = await svgStringToDataUrl(getEmojiSvgString(et, fi), 120);
            }
          }
        }
      }
    }

    // Pre-load image block URLs into data URLs for PDF rendering
    const imagePngCache: Record<string, string | null> = {};
    for (const section of definition.sections) {
      for (const block of section.blocks) {
        if (block.type === 'image' && block.src && !(block.src in imagePngCache)) {
          try {
            imagePngCache[block.src] = await urlToDataUrl(block.src);
          } catch {
            imagePngCache[block.src] = null; // null = failed, render placeholder
          }
        }
      }
    }

    // Pre-fetch video titles via oEmbed for YouTube and Vimeo
    const videoTitleCache: Record<string, string> = {};
    for (const section of definition.sections) {
      for (const block of section.blocks) {
        if (block.type === 'video' && block.src && !(block.src in videoTitleCache)) {
          try {
            const host = new URL(block.src).hostname.replace(/^www\./, '');
            let oEmbedUrl = '';
            if (host === 'youtube.com' || host === 'youtu.be')
              oEmbedUrl = `https://www.youtube.com/oembed?url=${encodeURIComponent(block.src)}&format=json`;
            else if (host === 'vimeo.com')
              oEmbedUrl = `https://vimeo.com/api/oembed.json?url=${encodeURIComponent(block.src)}`;
            if (oEmbedUrl) {
              const res = await fetch(oEmbedUrl);
              if (res.ok) {
                const data = (await res.json()) as { title?: string };
                if (data.title) videoTitleCache[block.src] = data.title;
              }
            }
          } catch {
            /* ignore */
          }
        }
      }
    }

    const pdfFields: FormState = blank ? {} : fields;
    const pdfSig: SignatureState = blank ? { kind: 'draw', dataUrl: '', text: '' } : signature;
    const pdfReceipt = blank ? null : finalReceipt;

    const doc = new jsPDF({ unit: 'pt', format: 'letter' });
    const W = doc.internal.pageSize.getWidth();
    const H = doc.internal.pageSize.getHeight();
    const M = 54;
    let y = M;

    doc.setFont('helvetica', 'bold');
    doc.setTextColor(11, 114, 133);
    doc.setFontSize(18);
    doc.text(applyLabel(definition.title || definition.pdfTitle), W / 2, y, { align: 'center' });
    y += 18;
    doc.setFont('helvetica', 'italic');
    doc.setTextColor(74, 85, 104);
    doc.setFontSize(10);
    doc.text(blank ? 'HIPAA LINK Teleconsent' : 'HIPAA LINK Teleconsent  •  Signed copy', W / 2, y, {
      align: 'center',
    });
    y += 24;
    doc.setDrawColor(226, 232, 240);
    doc.line(M, y, W - M, y);
    y += 18;

    const LINE_H = 14; // body line height (pt)

    const pushText = (text: string, bold = false, trailingGap = bold ? 4 : 10) => {
      doc.setFont('helvetica', bold ? 'bold' : 'normal');
      doc.setTextColor(56, 64, 77);
      doc.setFontSize(9.5);
      const lines = doc.splitTextToSize(text, W - M * 2);
      lines.forEach((line: string) => {
        if (y > H - 80) {
          doc.addPage();
          y = M;
        }
        doc.text(line, M, y);
        y += LINE_H;
      });
      y += trailingGap;
    };

    // Renders a bold label then normal value on the next line (for text/textarea/date/address/print-name fields)
    const pushLabelBlock = (label: string, value: string, trailing = 8) => {
      doc.setFontSize(9.5);
      doc.setTextColor(56, 64, 77);
      doc.setFont('helvetica', 'bold');
      const labelLines = doc.splitTextToSize(label, W - M * 2);
      labelLines.forEach((line: string) => {
        if (y > H - 80) {
          doc.addPage();
          y = M;
        }
        doc.text(line, M, y);
        y += LINE_H;
      });
      doc.setFont('helvetica', 'normal');
      const valueLines = doc.splitTextToSize(value || '—', W - M * 2);
      valueLines.forEach((line: string) => {
        if (y > H - 80) {
          doc.addPage();
          y = M;
        }
        doc.text(line, M, y);
        y += LINE_H;
      });
      y += trailing;
    };

    // Renders bold label + normal value on the same line (for select/radio/inline fields)
    const pushLabelInline = (label: string, value: string, trailing = 8) => {
      doc.setFontSize(9.5);
      doc.setTextColor(56, 64, 77);
      if (y > H - 80) {
        doc.addPage();
        y = M;
      }
      doc.setFont('helvetica', 'bold');
      const lw = doc.getTextWidth(label + ' ');
      doc.text(label, M, y);
      doc.setFont('helvetica', 'normal');
      const available = W - M * 2 - lw;
      const valueLines = doc.splitTextToSize(value || '—', available);
      doc.text(valueLines[0] ?? '', M + lw, y);
      y += LINE_H;
      for (let vi = 1; vi < valueLines.length; vi++) {
        if (y > H - 80) {
          doc.addPage();
          y = M;
        }
        doc.text(valueLines[vi], M + lw, y);
        y += LINE_H;
      }
      y += trailing;
    };

    const pushSectionHeading = (
      title: string,
      sectionNumber: number,
      sectionTitle?: string,
      hideSectionTitle?: boolean,
    ) => {
      // Extra space before each section to visually separate it from the previous
      y += 0;
      if (y > H - 140) {
        doc.addPage();
        y = M;
      }
      if (!hideSectionTitle) {
        const secLabel = sectionTitle || `Section ${sectionNumber}`;
        doc.setFont('helvetica', 'normal');
        doc.setTextColor(26, 36, 52);
        doc.setFontSize(8.5);
        doc.text(secLabel.toUpperCase(), M, y);
        y += 13;
      }
      doc.setFont('helvetica', 'bold');
      doc.setTextColor(26, 36, 52);
      doc.setFontSize(11);
      doc.text(title.toUpperCase(), M, y);
      y += 20; // generous gap between heading and first content block
    };

    if (definition.intro) {
      const provName = providerName?.trim() || (pdfFields['__providerName'] as string)?.trim() || 'your provider';
      const introParts = definition.intro.split('{providerName}');
      if (introParts.length === 2) {
        doc.setFontSize(9.5);
        doc.setTextColor(56, 64, 77);
        const maxW = W - M * 2;

        const renderWords = (text: string, cx: number): number => {
          doc.setFont('helvetica', 'normal');
          for (const token of text.split(/(\s+)/)) {
            if (!token) continue;
            const tw = doc.getTextWidth(token);
            if (token.trim() && cx > M && cx + tw > M + maxW) {
              y += LINE_H;
              if (y > H - 80) {
                doc.addPage();
                y = M;
              }
              cx = M;
            }
            if (token.trim()) doc.text(token, cx, y);
            cx += tw;
          }
          return cx;
        };

        let cx = M;
        cx = renderWords(introParts[0], cx);

        if (blank) {
          // Thin write-in line — same style as "Printed Name" blank line
          const SPACE = 6;
          const LINE_W = 180;
          cx += SPACE;
          if (cx + LINE_W > M + maxW) {
            y += LINE_H;
            if (y > H - 80) {
              doc.addPage();
              y = M;
            }
            cx = M + SPACE;
          }
          doc.setDrawColor(180, 195, 210);
          doc.setLineWidth(0.4);
          doc.line(cx, y + 2, cx + LINE_W, y + 2);
          cx += LINE_W + SPACE;
        } else {
          // Bold provider name inline
          doc.setFont('helvetica', 'bold');
          const nw = doc.getTextWidth(provName);
          if (cx + nw > M + maxW) {
            y += LINE_H;
            if (y > H - 80) {
              doc.addPage();
              y = M;
            }
            cx = M;
          }
          doc.text(provName, cx, y);
          cx += nw;
        }

        cx = renderWords(introParts[1], cx);
        y += LINE_H + 14;
      } else {
        pushText(definition.intro.replace('{providerName}', provName));
        y += 4;
      }
    }

    definition.sections.forEach((section, sectionIdx) => {
      const hasSignatureBlock = section.blocks.some((b) => b.type === 'signature');
      if (hasSignatureBlock && y > H - 200) {
        doc.addPage();
        y = M;
      }
      pushSectionHeading(section.heading, sectionIdx + 1, section.sectionTitle, section.hideSectionTitle);

      // Pre-process: merge adjacent builder name/initials blocks into one field-group for PDF table
      const PDF_NAME_IDS = new Set(['firstName', 'initials', 'lastName']);
      const isBuilderNameBlock = (b: ContentBlock): boolean => {
        if (b.type !== 'field-group') return false;
        if ((b as { pdfOnly?: boolean }).pdfOnly) return false;
        return b.fields.length === 1 && PDF_NAME_IDS.has(b.fields[0].id);
      };
      const pdfBlocks: ContentBlock[] = [];
      {
        let pi = 0;
        while (pi < section.blocks.length) {
          const pb = section.blocks[pi];
          if (isBuilderNameBlock(pb)) {
            const mergedFields: FormField[] = [(pb as { fields: FormField[] }).fields[0]];
            let qi = pi + 1;
            while (qi < section.blocks.length && isBuilderNameBlock(section.blocks[qi])) {
              mergedFields.push((section.blocks[qi] as { fields: FormField[] }).fields[0]);
              qi++;
            }
            if (mergedFields.length >= 2) {
              pdfBlocks.push({ type: 'field-group', fields: mergedFields });
              pi = qi;
              continue;
            }
          }
          pdfBlocks.push(pb);
          pi++;
        }
      }

      pdfBlocks.forEach((block) => {
        if (block.type === 'paragraph') {
          if (block.pdfOnly) return; // skip legacy office-use label paragraphs

          const pFontSize = block.fontSize ? Math.min(Math.round(block.fontSize * 0.72), 16) : 9.5;
          const pBold = block.bold ?? false;
          const pIndentPt = (block.indent ?? 0) * 10;
          const pAvailW = W - M * 2 - pIndentPt;
          const pAlign = block.align === 'center' || block.align === 'right' ? block.align : 'left';
          const pLineH = Math.max(Math.round(pFontSize * 1.47), LINE_H);
          const pTrailingGap = pBold ? 4 : 10;

          const parseHex = (hex: string): [number, number, number] | null => {
            if (!hex || !hex.startsWith('#') || hex.length < 7) return null;
            return [parseInt(hex.slice(1, 3), 16), parseInt(hex.slice(3, 5), 16), parseInt(hex.slice(5, 7), 16)];
          };
          const textRgb = parseHex(block.color ?? '') ?? ([56, 64, 77] as [number, number, number]);
          const hlRgb = parseHex(block.highlight ?? '');

          if (pBold) y += 6;
          doc.setFont('helvetica', pBold ? 'bold' : 'normal');
          doc.setFontSize(pFontSize);
          doc.setTextColor(textRgb[0], textRgb[1], textRgb[2]);

          const pLines = doc.splitTextToSize(injectVariables(block.text, varCtx), pAvailW);
          pLines.forEach((line: string) => {
            if (y > H - 80) {
              doc.addPage();
              y = M;
            }
            if (hlRgb) {
              const lw = doc.getTextWidth(line) + 4;
              let hlX = M + pIndentPt;
              if (pAlign === 'center') hlX = M + pIndentPt + (pAvailW - lw) / 2;
              else if (pAlign === 'right') hlX = M + pIndentPt + pAvailW - lw;
              doc.setFillColor(hlRgb[0], hlRgb[1], hlRgb[2]);
              doc.rect(hlX, y - pFontSize * 0.8, lw, pFontSize * 1.15, 'F');
              doc.setTextColor(textRgb[0], textRgb[1], textRgb[2]);
            }
            const tx =
              pAlign === 'center'
                ? M + pIndentPt + pAvailW / 2
                : pAlign === 'right'
                ? M + pIndentPt + pAvailW
                : M + pIndentPt;
            doc.text(line, tx, y, pAlign !== 'left' ? { align: pAlign } : undefined);
            y += pLineH;
          });
          y += pTrailingGap;
        } else if (block.type === 'bullet-list') {
          doc.setFont('helvetica', 'normal');
          if (block.color) {
            const r = parseInt(block.color.slice(1, 3), 16);
            const g = parseInt(block.color.slice(3, 5), 16);
            const b = parseInt(block.color.slice(5, 7), 16);
            doc.setTextColor(r, g, b);
          } else {
            doc.setTextColor(56, 64, 77);
          }
          const listFontSize = block.fontSize ? Math.min(Math.round(block.fontSize * 0.72), 14) : 9.5;
          doc.setFontSize(listFontSize);
          const listIndentPt = (block.indent ?? 0) * 10;
          const listIsNumbered = block.listStyle === 'numbered';
          const listIsLettered = block.listStyle === 'lettered';
          const listIsRoman = block.listStyle === 'roman';
          block.items.forEach((item, itemIdx) => {
            // Gap between items (skip before the first one)
            if (itemIdx > 0) y += 6;
            const lines = doc.splitTextToSize(item, W - M * 2 - 20 - listIndentPt);
            let prefix = '•';
            if (listIsNumbered) prefix = `${itemIdx + 1}.`;
            else if (listIsLettered) {
              const base = block.letterCase === 'upper' ? 65 : 97;
              prefix = `${String.fromCharCode(base + itemIdx)}.`;
            } else if (listIsRoman) prefix = `${toRoman(itemIdx + 1)}.`;
            lines.forEach((line: string, li: number) => {
              if (y > H - 80) {
                doc.addPage();
                y = M;
              }
              if (li === 0) doc.text(prefix, M + 8 + listIndentPt, y);
              doc.text(line, M + 20 + listIndentPt, y);
              y += LINE_H;
            });
          });
          y += 16; // trailing gap after the whole list
        } else if (block.type === 'callout') {
          y += 8; // space before callout
          pushText(`[${block.title}] ${block.body}`, false, 16);
        } else if (block.type === 'field-group') {
          if (block.pdfOnly) return; // skip legacy hardcoded office-use blocks
          y += 2;

          // Detect a name + DOB group and render inline
          const NAME_IDS = ['firstName', 'middleInitial', 'initials', 'lastName'];
          const nameFields = block.fields.filter((f) => NAME_IDS.includes(f.id));
          const dobField = block.fields.find((f) => f.id === 'dob');
          const restFields = block.fields.filter(
            (f) => !NAME_IDS.includes(f.id) && f.id !== 'dob' && f.type !== 'readonly' && f.type !== 'computed',
          );

          // Resolve a date field value — handles both ISO (yyyy-mm-dd for DOB) and mm/dd/yyyy (generic date)
          const resolveDateDisplay = (fieldId: string, rawVal: string | undefined): string => {
            if (!rawVal) return blank ? '' : '—';
            if (rawVal.includes('-')) {
              const [yyyy, mm, dd] = rawVal.split('-');
              return `${mm}/${dd}/${yyyy}`;
            }
            return rawVal; // already mm/dd/yyyy
          };

          if (block.pdfTable) {
            const availW = (W - M * 2) * (block.pdfTableWidth ?? 1);
            const PAD_H = 8;
            const MIN_COL_W = 60;
            const tableFields = block.fields.filter((f) => !('pdfHide' in f && f.pdfHide));
            const tableValues = tableFields.map((f) => {
              if (f.type === 'date') {
                return resolveDateDisplay(f.id, pdfFields[f.id] as string | undefined);
              }
              if (f.type === 'computed') return computeField(f.compute, pdfFields, f.sourceField);
              if (f.type === 'readonly') return resolveReadonly(f.value, varCtx);
              if (f.type === 'select') {
                const raw = typeof pdfFields[f.id] === 'string' ? (pdfFields[f.id] as string) : '';
                return f.options.find((o) => o.value === raw)?.label ?? (blank ? '' : f.options[0]?.label ?? '—');
              }
              const v = pdfFields[f.id];
              return typeof v === 'string' ? v || (blank ? '' : '—') : v ? 'Yes' : blank ? '' : '—';
            });
            doc.setFont('helvetica', 'normal');
            doc.setFontSize(7.5);
            const tLabelW = tableFields.map((f) => doc.getTextWidth(f.label));
            doc.setFontSize(10.5);
            const tValueW = tableValues.map((d) => doc.getTextWidth(d));
            const tColW = tableFields.map((f, i) => {
              const minW = 'pdfMinWidth' in f && f.pdfMinWidth ? f.pdfMinWidth : MIN_COL_W;
              return Math.max(Math.max(tLabelW[i], tValueW[i]) + PAD_H * 2, minW);
            });
            const tTotal = tColW.reduce((a, b) => a + b, 0);
            const tScale = tTotal > availW ? availW / tTotal : 1;
            const tFinalW = tColW.map((w) => w * tScale);
            const tCellH = 36;
            let tcx = M;
            if (y > H - tCellH - 80) {
              doc.addPage();
              y = M;
            }
            tableFields.forEach((f, i) => {
              const cw = tFinalW[i];
              doc.setLineWidth(0.5);
              doc.setDrawColor(203, 213, 224);
              doc.setFillColor(250, 252, 255);
              doc.rect(tcx, y, cw, tCellH, 'FD');
              doc.setFont('helvetica', 'normal');
              doc.setFontSize(7.5);
              doc.setTextColor(92, 103, 118);
              doc.text(f.label, tcx + PAD_H, y + 11);
              doc.setFont('helvetica', 'normal');
              doc.setFontSize(10.5);
              doc.setTextColor(26, 36, 52);
              if (tableValues[i]) {
                const vLines = doc.splitTextToSize(tableValues[i], cw - PAD_H * 2);
                doc.text(vLines[0] ?? '', tcx + PAD_H, y + 26);
              } else {
                doc.setDrawColor(180, 195, 210);
                doc.setLineWidth(0.4);
                doc.line(tcx + PAD_H, y + 29, tcx + cw - PAD_H, y + 29);
              }
              tcx += cw;
            });
            y += tCellH + 12;
          } else if (nameFields.length >= 2) {
            const availW = W - M * 2;
            const inlineFields = dobField ? [...nameFields, dobField] : nameFields;

            // Display values
            const displayValues = inlineFields.map((f) => {
              if (f.type === 'date') {
                return resolveDateDisplay(f.id, pdfFields[f.id] as string | undefined);
              }
              const v = pdfFields[f.id];
              return typeof v === 'string' ? v || (blank ? '' : '—') : blank ? '' : '—';
            });

            // Measure content to size each column to its content
            const PAD_H = 8;
            doc.setFont('helvetica', 'normal');
            doc.setFontSize(7.5);
            const labelWidths = inlineFields.map((f) => doc.getTextWidth(f.label));
            doc.setFontSize(10.5);
            const valueWidths = displayValues.map((d) => doc.getTextWidth(d));

            // firstName and lastName get at least 130pt; dob gets at least 110pt
            const MIN_NAME_W = 130;
            const MIN_DOB_W = 110;
            const colW = inlineFields.map((f, i) => {
              const natural = Math.max(labelWidths[i], valueWidths[i]) + PAD_H * 2;
              if (f.id === 'firstName' || f.id === 'lastName') return Math.max(natural, MIN_NAME_W);
              if (f.id === 'dob') return Math.max(natural, MIN_DOB_W);
              return natural;
            });

            // Scale down uniformly if total exceeds available width
            const totalW = colW.reduce((a, b) => a + b, 0);
            const scaleF = totalW > availW ? availW / totalW : 1;
            const finalColW = colW.map((w) => w * scaleF);

            // Draw table cells
            const cellH = 36;
            let cx = M;
            inlineFields.forEach((f, i) => {
              const cw = finalColW[i];
              doc.setLineWidth(0.5);
              doc.setDrawColor(203, 213, 224);
              doc.setFillColor(250, 252, 255);
              doc.rect(cx, y, cw, cellH, 'FD');

              // Label inside cell
              doc.setFont('helvetica', 'normal');
              doc.setFontSize(7.5);
              doc.setTextColor(92, 103, 118);
              doc.text(f.label, cx + PAD_H, y + 11);

              // Value inside cell
              doc.setFont('helvetica', 'normal');
              doc.setFontSize(10.5);
              doc.setTextColor(26, 36, 52);
              if (displayValues[i]) {
                const valLines = doc.splitTextToSize(displayValues[i], cw - PAD_H * 2);
                doc.text(valLines[0] ?? '', cx + PAD_H, y + 26);
              } else {
                doc.setDrawColor(180, 195, 210);
                doc.setLineWidth(0.4);
                doc.line(cx + PAD_H, y + 29, cx + cw - PAD_H, y + 29);
              }

              cx += cw;
            });
            y += cellH + 12;

            // Remaining fields render normally
            restFields.forEach((f) => {
              const v = pdfFields[f.id];
              // Skip provider-prefilled optional fields that were left blank
              if ('hideIfEmpty' in f && f.hideIfEmpty && (!v || (typeof v === 'string' && !v.trim()))) return;
              let display: string;

              if (f.type === 'date') {
                display = resolveDateDisplay(f.id, typeof v === 'string' ? v : undefined);
              } else if (f.type === 'select') {
                const raw = typeof v === 'string' ? v : '';
                const matched = (f as { options: { value: string; label: string }[] }).options.find(
                  (o) => o.value === raw,
                );
                display = (matched ?? (f as { options: { value: string; label: string }[] }).options[0])?.label ?? '—';
              } else {
                display = typeof v === 'string' ? v || '—' : v ? 'Yes' : '—';
              }

              if (f.type === 'text' || f.type === 'textarea') {
                pushLabelBlock(f.label, display, 8);
              } else {
                pushLabelInline(`${f.label}:`, display, 8);
              }
            });
          } else if (block.pdfOnly) {
            return; // skip legacy hardcoded office-use blocks (replaced by office-use block type)
          } else {
            // Default: each field on its own line
            block.fields.forEach((f) => {
              // ❗ EXCLUDE signature-related fields (prevents duplicate rendering)
              if (f.type === 'readonly' || f.type === 'computed') return;
              if (['signature', 'printedName', 'relationship', 'guardianPrintedName', 'guardianRole'].includes(f.id))
                return;

              const v = pdfFields[f.id];
              // Skip provider-prefilled optional fields that were left blank
              if ('hideIfEmpty' in f && f.hideIfEmpty && (!v || (typeof v === 'string' && !v.trim()))) return;

              let display: string;

              if (f.type === 'date') {
                display = resolveDateDisplay(f.id, typeof v === 'string' ? v : undefined);
              } else if (f.type === 'select') {
                const raw = typeof v === 'string' ? v : '';
                const matched = f.options.find((o) => o.value === raw);
                display = (matched ?? f.options[0])?.label ?? '—';
              } else {
                display = typeof v === 'string' ? v || '—' : v ? 'Yes' : '—';
              }

              if (f.type === 'text' || f.type === 'textarea') {
                pushLabelBlock(f.label, display, 8);
              } else {
                pushLabelInline(`${f.label}:`, display, 8);
              }
            });
          }
          y += 8;
        } else if (block.type === 'checklist') {
          y += 2;
          const BOX = 7;
          const CHKINDENT = 16;
          const isRadio = block.variant === 'radio';
          block.options.forEach((opt) => {
            if (y > H - 80) {
              doc.addPage();
              y = M;
            }
            const checked = Boolean(pdfFields[`${block.id}:${opt.id}`]);
            doc.setDrawColor(100, 110, 120);
            doc.setFillColor(255, 255, 255);
            doc.setLineWidth(0.6);
            if (isRadio) {
              const cx = M + CHKINDENT + BOX / 2;
              const cy = y - BOX / 2 + 1;
              doc.circle(cx, cy, BOX / 2, 'FD');
              if (checked) {
                doc.setFillColor(11, 114, 133);
                doc.circle(cx, cy, BOX / 2 - 1.8, 'F');
              }
            } else {
              doc.rect(M + CHKINDENT, y - BOX + 1, BOX, BOX, 'FD');
              if (checked) {
                doc.setDrawColor(11, 114, 133);
                doc.setLineWidth(1.3);
                doc.line(M + CHKINDENT + 1.5, y - 2.8, M + CHKINDENT + 3, y - 1.2);
                doc.line(M + CHKINDENT + 3, y - 1.2, M + CHKINDENT + 6.5, y - 5.8);
              }
            }
            doc.setFont('helvetica', 'normal');
            doc.setTextColor(56, 64, 77);
            doc.setFontSize(9.5);
            const optLines = doc.splitTextToSize(opt.label, W - M * 2 - CHKINDENT - 14);
            optLines.forEach((line: string) => {
              doc.text(line, M + CHKINDENT + 14, y);
              y += LINE_H;
            });
            y += 2;
          });
          y += 2;
        } else if (block.type === 'signature') {
          // Ensure the full signature block fits on the page; 220pt covers
          // the text lines, sig box, and receipt lines in a single column.
          if (y > H - 220) {
            doc.addPage();
            y = M;
          }

          const usesGuardianFields = !pdfFields['printedName'] && !!pdfFields['guardianPrintedName'];
          const printedName = ((usesGuardianFields ? pdfFields['guardianPrintedName'] : pdfFields['printedName']) ||
            (blank ? '' : '—')) as string;

          // Resolve relationship/role value → display label via form definition
          const rawRel = usesGuardianFields
            ? (pdfFields['guardianRole'] as string) || ''
            : (pdfFields['relationship'] as string) || '';
          const relFieldId = usesGuardianFields ? 'guardianRole' : 'relationship';
          const nameFieldId = usesGuardianFields ? 'guardianPrintedName' : 'printedName';
          let relationship = blank ? '' : '—';
          let nameLabelPdf = 'Printed Name:';
          let relLabelPdf = usesGuardianFields ? 'Legal Role:' : `Relationship to ${applyLabel('Patient')}:`;
          if (!blank) {
            for (const sec of definition.sections) {
              for (const blk of sec.blocks) {
                if (blk.type === 'field-group') {
                  const nameF = blk.fields.find((ff) => ff.id === nameFieldId);
                  if (nameF?.label) {
                    const l = nameF.label.trim();
                    nameLabelPdf = l.endsWith(':') ? l : `${l}:`;
                  }
                  const relF = blk.fields.find((ff) => ff.id === relFieldId && ff.type === 'select');
                  if (relF && relF.type === 'select') {
                    const matched = relF.options.find((o) => o.value === rawRel);
                    relationship = matched?.label ?? relF.options[0]?.label ?? '—';
                    const rl = relF.label?.trim() || '';
                    if (rl) relLabelPdf = rl.endsWith(':') ? rl : `${rl}:`;
                  }
                }
              }
            }
          }

          // Pull back 4pt — section heading already added 20pt gap
          y -= 4;

          const printLine = (label: string, value: string) => {
            doc.setFont('helvetica', 'bold');
            doc.setFontSize(9);
            doc.setTextColor(92, 103, 118);
            doc.text(label, M, y);
            const lw = doc.getTextWidth(label);
            if (value) {
              doc.setTextColor(26, 36, 52);
              doc.text(value, M + lw + 6, y);
            } else {
              doc.setDrawColor(180, 195, 210);
              doc.setLineWidth(0.4);
              doc.line(M + lw + 6, y + 2, M + lw + 140, y + 2);
            }
            y += 14;
          };

          if (!definition.isCustomForm) {
            printLine(nameLabelPdf, printedName);
            printLine(relLabelPdf, relationship);
            y += 14; // space between relationship line and signature box
          }

          // Signature box — full content width, fixed height
          const sigBoxW = (W - M * 2) * 0.5;
          const sigBoxH = 90;

          doc.setDrawColor(203, 213, 224);
          doc.setLineWidth(0.8);
          doc.rect(M, y, sigBoxW, sigBoxH);

          // "x ——" signing prompt — shown when there is no signature to display
          if (!pdfSig.dataUrl && !pdfSig.text) {
            const xPt = M + 10;
            const xLineY = y + sigBoxH - 20;
            doc.setFont('helvetica', 'normal');
            doc.setFontSize(11);
            doc.setTextColor(120, 130, 140);
            doc.text('x', xPt, xLineY);
            const xCharW = doc.getTextWidth('x');
            doc.setDrawColor(120, 130, 140);
            doc.setLineWidth(0.7);
            doc.line(xPt + xCharW + 3, xLineY + 1, M + sigBoxW - 12, xLineY + 1);
            doc.setFontSize(7);
            doc.setTextColor(150, 160, 170);
            doc.text('sign here', xPt, xLineY + 11);
          }

          if (pdfSig.kind === 'draw' && pdfSig.dataUrl) {
            try {
              const imgFormat = pdfSig.dataUrl.startsWith('data:image/jpeg') ? 'JPEG' : 'PNG';
              const props = doc.getImageProperties(pdfSig.dataUrl);

              const maxW = sigBoxW - 16;
              const maxH = sigBoxH - 12;

              const scale = Math.min(maxW / props.width, maxH / props.height);
              const imgW = props.width * scale;
              const imgH = props.height * scale;

              const imgX = M + (sigBoxW - imgW) / 2;
              const imgY = y + (sigBoxH - imgH) / 2;

              doc.addImage(pdfSig.dataUrl, imgFormat, imgX, imgY, imgW, imgH);
            } catch {
              // ignore
            }
          } else if (pdfSig.kind === 'type' && pdfSig.text) {
            doc.setFont('times', 'italic');
            doc.setTextColor(15, 26, 42);
            doc.setFontSize(28);
            doc.text(pdfSig.text, M + 10, y + 55);
          }

          y += sigBoxH + 14; // advance past the box

          if (pdfReceipt) {
            doc.setFont('helvetica', 'normal');
            doc.setFontSize(9.5);

            doc.setTextColor(92, 103, 118);
            doc.text('Signed On:', M, y);

            doc.setTextColor(26, 36, 52);
            doc.text(new Date(pdfReceipt.submittedAt).toLocaleString(), M + 90, y);

            y += 14;

            doc.setTextColor(92, 103, 118);
            doc.text('Signed From IP:', M, y);

            doc.setTextColor(26, 36, 52);
            doc.text(pdfReceipt.clientIp, M + 90, y);
            y += 14;
          }
        } else if (block.type === 'address') {
          const enabledFields = block.subfields.filter((sf) => sf.enabled);
          if (enabledFields.length > 0) {
            y += 4;
            enabledFields.forEach((sf) => {
              const raw = pdfFields[`${block.id}:${sf.key}`];
              let display: string;
              if (sf.fieldType === 'select-country') {
                const code = typeof raw === 'string' ? raw : 'US';
                display = COUNTRIES.find((c) => c.value === code)?.label ?? code;
              } else {
                display = typeof raw === 'string' ? raw || (blank ? '' : '—') : blank ? '' : '—';
              }
              pushLabelInline(`${sf.label}:`, display, 6);
            });
            y += 6;
          }
        } else if (block.type === 'print-name') {
          const signingAsOptions = block.signingAsOptions || [];
          const nameVal = pdfFields[`${block.id}:name`];
          const signingAsVal = pdfFields[`${block.id}:signingAs`];
          const nameDisplay = typeof nameVal === 'string' ? nameVal || (blank ? '' : '—') : blank ? '' : '—';
          const matchedOption =
            typeof signingAsVal === 'string' ? signingAsOptions.find((o) => o.value === signingAsVal) : undefined;
          const signingAsDisplay = matchedOption ? matchedOption.label : blank ? '' : '—';
          const nameLabel = block.nameLabel || 'Your Name (print):';
          const signingAsLabel = block.signingAsLabel || 'Signing as:';
          pushLabelInline(nameLabel, nameDisplay, 6);
          pushLabelInline(signingAsLabel, signingAsDisplay, 6);
          if (block.acknowledgment) {
            y += 4;
            doc.setFont('helvetica', 'italic');
            doc.setFontSize(8);
            doc.setTextColor(92, 103, 118);
            const ackLines = doc.splitTextToSize(block.acknowledgment, W - M * 2);
            doc.text(ackLines, M, y);
            y += ackLines.length * 5 + 8;
          }
        } else if (block.type === 'time-field') {
          const tf = block as typeof block & { id: string; label: string; use24hr: boolean };
          const raw = pdfFields[tf.id] as string | undefined;
          const display = raw || '—';
          pushLabelInline(`${tf.label}:`, display, 8);
        } else if (block.type === 'scale') {
          y += 6;
          const raw = pdfFields[block.id];
          const selectedNum = typeof raw === 'string' && raw ? parseFloat(raw) : NaN;
          const scaleBlock = block as typeof block & {
            scaleType: string;
            min: number;
            max: number;
            step: number;
            leftLabel: string;
            rightLabel: string;
            orientation?: string;
            emojiCount?: number;
            emojiLabels?: string[];
            likertOptions?: string[];
          };

          // Label
          if (block.label) {
            doc.setFont('helvetica', 'bold');
            doc.setFontSize(9);
            doc.setTextColor(40, 52, 68);
            const lblLines = doc.splitTextToSize(block.label, W - M * 2);
            doc.text(lblLines, M, y);
            y += lblLines.length * 5 + 4;
          }

          if (scaleBlock.scaleType === 'numeric') {
            const steps: number[] = [];
            for (let n = scaleBlock.min; n <= scaleBlock.max; n += scaleBlock.step)
              steps.push(Math.round(n * 100) / 100);
            const btnW = 22;
            const btnH = 22;
            const gap = 4;
            const totalW = steps.length * (btnW + gap) - gap;
            if (y + btnH + 14 > H - 80) {
              doc.addPage();
              y = M;
            }
            let bx = M;
            steps.forEach((n) => {
              const sel = !isNaN(selectedNum) && n === selectedNum;
              doc.setFillColor(sel ? 11 : 250, sel ? 114 : 252, sel ? 133 : 255);
              doc.setDrawColor(sel ? 11 : 203, sel ? 114 : 213, sel ? 133 : 224);
              doc.setLineWidth(0.5);
              doc.roundedRect(bx, y, btnW, btnH, 3, 3, 'FD');
              doc.setFont('helvetica', sel ? 'bold' : 'normal');
              doc.setFontSize(8);
              doc.setTextColor(sel ? 255 : 80, sel ? 255 : 90, sel ? 255 : 100);
              doc.text(String(n), bx + btnW / 2, y + btnH / 2 + 3, { align: 'center' });
              bx += btnW + gap;
            });
            y += btnH + 5;
            // end labels under the buttons row
            if (scaleBlock.leftLabel || scaleBlock.rightLabel) {
              doc.setFont('helvetica', 'normal');
              doc.setFontSize(7.5);
              doc.setTextColor(139, 149, 163);
              if (scaleBlock.leftLabel) doc.text(scaleBlock.leftLabel, M, y + 5);
              if (scaleBlock.rightLabel) doc.text(scaleBlock.rightLabel, M + totalW, y + 5, { align: 'right' });
              y += 10;
            }
            y += 10;
          } else if (scaleBlock.scaleType === 'stars') {
            const count = scaleBlock.max - scaleBlock.min + 1;
            const starSize = 24;
            const gap = 7;
            const totalW = count * (starSize + gap) - gap;
            if (y + starSize + 14 > H - 80) {
              doc.addPage();
              y = M;
            }
            // Draw 5-point star polygons (jsPDF can't render ★ Unicode in standard fonts)
            const drawStar = (cx: number, cy: number, outerR: number, filled: boolean) => {
              const innerR = outerR * 0.4;
              const pts: number[][] = [];
              for (let p = 0; p < 10; p++) {
                const angle = (Math.PI / 5) * p - Math.PI / 2;
                const r = p % 2 === 0 ? outerR : innerR;
                pts.push([cx + r * Math.cos(angle), cy + r * Math.sin(angle)]);
              }
              if (filled) {
                doc.setFillColor(245, 158, 11);
                doc.setDrawColor(220, 130, 0);
              } else {
                doc.setFillColor(220, 220, 220);
                doc.setDrawColor(180, 180, 180);
              }
              doc.setLineWidth(0.3);
              // draw polygon via lines
              doc.moveTo(pts[0][0], pts[0][1]);
              for (let p = 1; p < pts.length; p++) doc.lineTo(pts[p][0], pts[p][1]);
              doc.lineTo(pts[0][0], pts[0][1]);
              (doc as unknown as { fillStroke: () => void }).fillStroke?.();
              // fallback: draw as filled triangles from center
              doc.setFillColor(filled ? 245 : 220, filled ? 158 : 220, filled ? 11 : 220);
              const lines: number[][] = [];
              for (let p = 1; p < pts.length; p++) {
                lines.push([pts[p][0] - pts[p - 1][0], pts[p][1] - pts[p - 1][1]]);
              }
              doc.lines(lines as [number, number][], pts[0][0], pts[0][1], [1, 1], filled ? 'F' : 'S', true);
            };
            let sx = M + starSize / 2;
            for (let i = scaleBlock.min; i <= scaleBlock.max; i++) {
              const filled = !isNaN(selectedNum) && selectedNum >= i;
              drawStar(sx, y + starSize / 2, starSize / 2, filled);
              sx += starSize + gap;
            }
            y += starSize + 5;
            if (scaleBlock.leftLabel || scaleBlock.rightLabel) {
              doc.setFont('helvetica', 'normal');
              doc.setFontSize(7.5);
              doc.setTextColor(139, 149, 163);
              if (scaleBlock.leftLabel) doc.text(scaleBlock.leftLabel, M, y + 5);
              if (scaleBlock.rightLabel) doc.text(scaleBlock.rightLabel, M + totalW, y + 5, { align: 'right' });
              y += 10;
            }
            y += 10;
          } else if (scaleBlock.scaleType === 'slider') {
            const trackY = y + 8;
            const trackW = W - M * 2 - 30;
            if (y + 24 > H - 80) {
              doc.addPage();
              y = M;
            }
            doc.setDrawColor(203, 213, 224);
            doc.setLineWidth(2);
            doc.line(M, trackY, M + trackW, trackY);
            if (!isNaN(selectedNum)) {
              const frac = (selectedNum - scaleBlock.min) / (scaleBlock.max - scaleBlock.min);
              const cx = M + frac * trackW;
              doc.setFillColor(11, 114, 133);
              doc.circle(cx, trackY, 4, 'F');
              doc.setFont('helvetica', 'bold');
              doc.setFontSize(8);
              doc.setTextColor(11, 114, 133);
              doc.text(String(selectedNum), M + trackW + 6, trackY + 3);
            }
            y += 18;
            if (scaleBlock.leftLabel || scaleBlock.rightLabel) {
              doc.setFont('helvetica', 'normal');
              doc.setFontSize(7.5);
              doc.setTextColor(139, 149, 163);
              if (scaleBlock.leftLabel) doc.text(scaleBlock.leftLabel, M, y + 5);
              if (scaleBlock.rightLabel) doc.text(scaleBlock.rightLabel, M + trackW, y + 5, { align: 'right' });
              y += 10;
            }
            y += 10;
          } else if (scaleBlock.scaleType === 'emoji') {
            const labels: string[] =
              scaleBlock.emojiLabels ??
              (scaleBlock.emojiCount === 3
                ? ['Sad', 'Neutral', 'Happy']
                : ['Very Sad', 'Sad', 'Neutral', 'Happy', 'Very Happy']);
            const total = scaleBlock.emojiCount ?? 5;
            const et = scaleBlock.emojiType ?? 'emoji';
            const faceSize = 22; // pt — image square size in PDF
            const colW = Math.max(faceSize + 6, Math.min(52, (W - M * 2) / total));
            const rowH = faceSize + 14; // image + label space
            if (y + rowH > H - 80) {
              doc.addPage();
              y = M;
            }
            const cachedPngs = emojiPngCache[et] ?? {};
            labels.slice(0, total).forEach((lbl, idx) => {
              const val = scaleBlock.min + ((scaleBlock.max - scaleBlock.min) / (total - 1)) * idx;
              const rounded = Math.round(val * 10) / 10;
              const sel = !isNaN(selectedNum) && rounded === selectedNum;
              const imgX = M + idx * colW + (colW - faceSize) / 2;
              const imgY = y + 1;
              const cx = M + idx * colW + colW / 2;
              if (sel) {
                doc.setFillColor(232, 248, 245);
                doc.setDrawColor(11, 114, 133);
                doc.setLineWidth(0.5);
                doc.roundedRect(M + idx * colW + 1, y - 1, colW - 2, rowH, 3, 3, 'FD');
              }
              const dataUrl = cachedPngs[idx];
              if (dataUrl) {
                doc.addImage(dataUrl, 'PNG', imgX, imgY, faceSize, faceSize);
              }
              // label
              doc.setFont('helvetica', sel ? 'bold' : 'normal');
              doc.setFontSize(6.5);
              doc.setTextColor(sel ? 11 : 90, sel ? 114 : 100, sel ? 133 : 110);
              const lblLines = doc.splitTextToSize(lbl, colW - 4);
              doc.text(lblLines[0] ?? '', cx, imgY + faceSize + 5, { align: 'center' });
            });
            y += rowH + 6;
          } else if (scaleBlock.scaleType === 'likert') {
            const opts: string[] = scaleBlock.likertOptions ?? [
              'Strongly Disagree',
              'Disagree',
              'Neutral',
              'Agree',
              'Strongly Agree',
            ];
            const btnW = Math.min(60, (W - M * 2 - (opts.length - 1) * 4) / opts.length);
            const btnH = 28;
            if (y + btnH + 6 > H - 80) {
              doc.addPage();
              y = M;
            }
            let bx = M;
            opts.forEach((opt, idx) => {
              const n = scaleBlock.min + idx;
              const sel = !isNaN(selectedNum) && n === selectedNum;
              doc.setFillColor(sel ? 11 : 250, sel ? 114 : 252, sel ? 133 : 255);
              doc.setDrawColor(sel ? 11 : 203, sel ? 114 : 213, sel ? 133 : 224);
              doc.setLineWidth(0.5);
              doc.roundedRect(bx, y, btnW, btnH, 3, 3, 'FD');
              doc.setFont('helvetica', sel ? 'bold' : 'normal');
              doc.setFontSize(6.5);
              doc.setTextColor(sel ? 255 : 80, sel ? 255 : 90, sel ? 255 : 100);
              const optLines = doc.splitTextToSize(opt, btnW - 6);
              const lineH = 6.5;
              const totalTextH = optLines.slice(0, 2).length * lineH;
              const textStartY = y + (btnH - totalTextH) / 2 + lineH * 0.75;
              optLines.slice(0, 2).forEach((line: string, li: number) => {
                doc.text(line, bx + btnW / 2, textStartY + li * lineH, { align: 'center' });
              });
              bx += btnW + 4;
            });
            y += btnH + 12;
          }
        } else if (block.type === 'matrix') {
          const mx = block as typeof block & {
            type: 'matrix';
            id: string;
            label?: string;
            columns: string[];
            rows: { id: string; text: string }[];
          };
          y += 6;
          if (mx.label) {
            doc.setFont('helvetica', 'bold');
            doc.setFontSize(9);
            doc.setTextColor(40, 52, 68);
            const lblLines = doc.splitTextToSize(mx.label, W - M * 2);
            doc.text(lblLines, M, y);
            y += lblLines.length * 5 + 4;
          }
          const availW = W - M * 2;
          const ROW_LABEL_W = availW * 0.38;
          const colsCount = mx.columns.length;
          const colW = (availW - ROW_LABEL_W) / Math.max(1, colsCount);
          const HEADER_H = 18;
          const ROW_H = 28;
          const RADIO_R = 3.5;
          const tableH = HEADER_H + mx.rows.length * ROW_H;
          if (y + tableH > H - 80) {
            doc.addPage();
            y = M;
          }
          const tableTop = y;
          // header row background
          doc.setFillColor(248, 250, 252);
          doc.setDrawColor(203, 213, 224);
          doc.setLineWidth(0.4);
          doc.rect(M, tableTop, availW, HEADER_H, 'FD');
          // header column labels — centered in each column cell
          doc.setFont('helvetica', 'bold');
          doc.setFontSize(7);
          doc.setTextColor(80, 90, 105);
          mx.columns.forEach((col, ci) => {
            const cx = M + ROW_LABEL_W + ci * colW + colW / 2;
            const colLines = doc.splitTextToSize(col, colW - 4);
            doc.text(colLines[0] ?? col, cx, tableTop + HEADER_H / 2 + 2.5, { align: 'center' });
          });
          // data rows
          mx.rows.forEach((row, ri) => {
            const rowY = tableTop + HEADER_H + ri * ROW_H;
            if (ri % 2 === 1) {
              doc.setFillColor(250, 252, 255);
              doc.setDrawColor(203, 213, 224);
              doc.setLineWidth(0.4);
              doc.rect(M, rowY, availW, ROW_H, 'FD');
            }
            // horizontal row separator
            doc.setDrawColor(203, 213, 224);
            doc.setLineWidth(0.3);
            doc.line(M, rowY, M + availW, rowY);
            // statement text — dark ink
            doc.setFont('helvetica', 'normal');
            doc.setFontSize(8);
            doc.setTextColor(40, 52, 68);
            const rowTextLines = doc.splitTextToSize(row.text, ROW_LABEL_W - 8);
            doc.text(rowTextLines[0] ?? '', M + 5, rowY + ROW_H / 2 + 3);
            // radio circles
            const selectedCol = pdfFields[`${mx.id}:${row.id}`] as string | undefined;
            mx.columns.forEach((col, ci) => {
              const cx = M + ROW_LABEL_W + ci * colW + colW / 2;
              const cy = rowY + ROW_H / 2;
              const isSel = selectedCol === col;
              doc.setLineWidth(0.6);
              if (isSel) {
                doc.setFillColor(11, 114, 133);
                doc.setDrawColor(11, 114, 133);
                doc.circle(cx, cy, RADIO_R, 'FD');
              } else {
                doc.setDrawColor(150, 160, 175);
                doc.circle(cx, cy, RADIO_R, 'S');
              }
            });
          });
          // vertical column separator lines spanning full table height
          doc.setDrawColor(203, 213, 224);
          doc.setLineWidth(0.4);
          doc.line(M + ROW_LABEL_W, tableTop, M + ROW_LABEL_W, tableTop + tableH);
          for (let ci = 1; ci < colsCount; ci++) {
            const lx = M + ROW_LABEL_W + ci * colW;
            doc.line(lx, tableTop, lx, tableTop + tableH);
          }
          // outer border
          doc.rect(M, tableTop, availW, tableH, 'S');
          y = tableTop + tableH + 10;
        } else if (block.type === 'office-use') {
          if (!isHost) return;
          const ouLabel = (block.label || 'For Office Use').toUpperCase();
          y += 18;
          pushText(ouLabel, true, 2);
          const ouCols = block.columns;
          if (ouCols.length > 0) {
            const availW = W - M * 2;
            const PAD = 8;
            const CELL_H = 38;
            const totalPct = ouCols.reduce((sum, col) => sum + (col.width ?? 0), 0);
            const usePercentages = totalPct > 0;
            const ouColWidths = ouCols.map((col) =>
              usePercentages && col.width ? (col.width / 100) * availW : availW / ouCols.length,
            );
            if (y > H - CELL_H - 80) {
              doc.addPage();
              y = M;
            }
            let ouCx = M;
            ouCols.forEach((col, i) => {
              const cw = ouColWidths[i];
              doc.setDrawColor(203, 213, 224);
              doc.setLineWidth(0.5);
              doc.setFillColor(250, 252, 255);
              doc.rect(ouCx, y, cw, CELL_H, 'FD');
              doc.setFont('helvetica', 'normal');
              doc.setFontSize(7.5);
              doc.setTextColor(92, 103, 118);
              const lblLines = doc.splitTextToSize(col.label, cw - PAD * 2);
              doc.text(lblLines[0] ?? '', ouCx + PAD, y + 11);
              doc.setDrawColor(180, 195, 210);
              doc.setLineWidth(0.4);
              doc.line(ouCx + PAD, y + 29, ouCx + cw - PAD, y + 29);
              ouCx += cw;
            });
            y += CELL_H + 12;
          }
        } else if (block.type === 'header') {
          // Scale form-builder CSS-px values to PDF pt
          const mt = Math.round((block.marginTop ?? 8) * 0.45);
          const mb = Math.round((block.marginBottom ?? 4) * 0.45);
          const hFontSize = Math.min(Math.round((block.fontSize ?? 30) * 0.45), 18);
          y += mt;
          if (y > H - 80) {
            doc.addPage();
            y = M;
          }
          doc.setFont('helvetica', 'bold');
          doc.setFontSize(hFontSize);
          doc.setTextColor(22, 31, 46);
          const hLines = doc.splitTextToSize(block.text, W - M * 2);
          hLines.forEach((line: string) => {
            if (y > H - 80) {
              doc.addPage();
              y = M;
            }
            doc.text(line, M, y);
            y += Math.round(hFontSize * 1.15);
          });
          doc.setTextColor(56, 64, 77);
          y += mb;
        } else if (block.type === 'divider') {
          // Every preceding text block advances y by LINE_H after its last line,
          // which (combined with that block's trailing gap) creates an oversized gap
          // before the divider. Pull back LINE_H to restore a balanced visual margin.
          y -= LINE_H;
          const mt = 4;
          const mb = 26;
          y += mt;
          if (y > H - 80) {
            doc.addPage();
            y = M;
          }
          const lineLen = (W - M * 2) * ((block.lengthPercent ?? 100) / 100);
          doc.setLineWidth(block.thickness ?? 0.5);
          doc.setDrawColor(226, 232, 240);
          doc.line(M, y, M + lineLen, y);
          doc.setDrawColor(0);
          doc.setLineWidth(1);
          y += mb;
        } else if (block.type === 'image') {
          y -= LINE_H - 8; // compensate for paragraph trailing advance; net ~8pt gap from last text
          const dataUrl = imagePngCache[block.src];
          if (dataUrl) {
            try {
              const props = doc.getImageProperties(dataUrl);
              const maxW = (W - M * 2) * ((block.widthPercent ?? 100) / 100);
              const aspect = props.height / props.width;
              const imgW = Math.min(maxW, W - M * 2);
              const imgH = imgW * aspect;
              if (y + imgH > H - 80) {
                doc.addPage();
                y = M;
              }
              const imgX =
                block.alignment === 'center'
                  ? M + (W - M * 2 - imgW) / 2
                  : block.alignment === 'right'
                  ? W - M - imgW
                  : M;
              doc.addImage(dataUrl, 'PNG', imgX, y, imgW, imgH);
              y += imgH + 6;
              if (block.alt) {
                doc.setFont('helvetica', 'italic');
                doc.setFontSize(8);
                doc.setTextColor(139, 149, 163);
                doc.text(block.alt, W / 2, y, { align: 'center' });
                y += 12;
              }
            } catch {
              /* skip image if rendering fails */
            }
          } else {
            // Placeholder for images blocked by CORS or invalid URLs — show like video block
            const hasAlt = Boolean(block.alt);
            const imgBoxH = hasAlt && block.src ? 42 : hasAlt || block.src ? 42 : 30;
            if (y + imgBoxH > H - 80) {
              doc.addPage();
              y = M;
            }
            doc.setFillColor(245, 247, 250);
            doc.setDrawColor(203, 213, 224);
            doc.setLineWidth(0.5);
            doc.roundedRect(M, y, W - M * 2, imgBoxH, 3, 3, 'FD');
            doc.setFont('helvetica', 'bold');
            doc.setFontSize(7.5);
            doc.setTextColor(74, 85, 104);
            doc.text('IMAGE', M + 10, y + 11);
            if (hasAlt) {
              doc.setFont('helvetica', 'normal');
              doc.setFontSize(9);
              doc.setTextColor(22, 31, 46);
              doc.text(block.alt!, M + 10, y + 23);
              if (block.src) {
                doc.setFontSize(7.5);
                doc.setTextColor(11, 114, 133);
                const srcLines = doc.splitTextToSize(block.src, W - M * 2 - 20);
                doc.text(srcLines[0] ?? '', M + 10, y + 35);
              }
            } else if (block.src) {
              doc.setFont('helvetica', 'normal');
              doc.setFontSize(7.5);
              doc.setTextColor(11, 114, 133);
              const srcLines = doc.splitTextToSize(block.src, W - M * 2 - 20);
              doc.text(srcLines[0] ?? '', M + 10, y + 22);
            }
            doc.setTextColor(56, 64, 77);
            y += imgBoxH + 22;
          }
        } else if (block.type === 'video') {
          y += 4;
          const videoTitle = videoTitleCache[block.src ?? ''];
          const hasTitle = Boolean(videoTitle);
          const boxH = hasTitle ? 42 : 30;
          if (y + boxH > H - 80) {
            doc.addPage();
            y = M;
          }
          doc.setFillColor(245, 247, 250);
          doc.setDrawColor(203, 213, 224);
          doc.setLineWidth(0.5);
          doc.roundedRect(M, y, W - M * 2, boxH, 3, 3, 'FD');
          doc.setFont('helvetica', 'bold');
          doc.setFontSize(7.5);
          doc.setTextColor(74, 85, 104);
          doc.text('VIDEO', M + 10, y + 11);
          if (hasTitle) {
            doc.setFont('helvetica', 'normal');
            doc.setFontSize(9);
            doc.setTextColor(22, 31, 46);
            const titleLines = doc.splitTextToSize(videoTitle, W - M * 2 - 20);
            doc.text(titleLines[0] ?? '', M + 10, y + 23);
            if (block.src) {
              doc.setFontSize(7.5);
              doc.setTextColor(11, 114, 133);
              const srcLines = doc.splitTextToSize(block.src, W - M * 2 - 20);
              doc.text(srcLines[0] ?? '', M + 10, y + 35);
            }
          } else {
            if (block.src) {
              doc.setFont('helvetica', 'normal');
              doc.setFontSize(7.5);
              doc.setTextColor(11, 114, 133);
              const srcLines = doc.splitTextToSize(block.src, W - M * 2 - 20);
              doc.text(srcLines[0] ?? '', M + 10, y + 22);
            }
          }
          doc.setTextColor(56, 64, 77);
          y += boxH + 22;
        }
      });
      y += 8;
    });

    // Draw footer on every page
    const totalPages = doc.getNumberOfPages();
    for (let pg = 1; pg <= totalPages; pg++) {
      doc.setPage(pg);
      doc.setDrawColor(226, 232, 240);
      doc.line(M, H - 60, W - M, H - 60);
      doc.setFontSize(8);
      doc.setTextColor(139, 149, 163);
      if (!blank) {
        doc.text('Submitted via HIPAA LINK Teleconsent', M, H - 44);
        doc.text(`Signed: ${pdfReceipt?.clientIp ?? '—'}  •  ${today()}  •  Page ${pg}/${totalPages}`, W - M, H - 44, {
          align: 'right',
        });
      } else {
        doc.text(`Page ${pg}/${totalPages}`, W - M, H - 44, { align: 'right' });
      }
    }

    const safeName = (definition.title || definition.pdfTitle).replace(/\s+/g, '-').replace(/[^\w\-().]/g, '');
    doc.save(`Copy-${safeName}.pdf`);
  };

  const handleDownload = async () => {
    setDownloadingPdf(true);
    await new Promise((resolve) => setTimeout(resolve, 50));
    try {
      await buildPdf();
      onDownload?.();
    } finally {
      setDownloadingPdf(false);
    }
  };

  const handleDownloadBlank = async () => {
    setDownloadingBlank(true);
    await new Promise((resolve) => setTimeout(resolve, 50));
    try {
      await buildPdf(true);
    } finally {
      setDownloadingBlank(false);
    }
  };

  // ---- render helpers ----
  const renderField = (f: FormField) => {
    // Skip provider-prefilled optional fields that were left blank
    if ('hideIfEmpty' in f && f.hideIfEmpty) {
      const v = fields[f.id];
      if (!v || (typeof v === 'string' && !v.trim())) return null;
    }

    const invalid = showErr && definition.requiredFields.includes(f.id) && !fields[f.id];

    const baseClass = [styles.field, 'fullWidth' in f && f.fullWidth ? styles.full : '', invalid ? styles.invalid : '']
      .filter(Boolean)
      .join(' ');

    if (f.type === 'readonly') {
      return (
        <div key={f.id} className={baseClass}>
          <label>{f.label}</label>
          <input type='text' readOnly value={resolveReadonly(f.value, varCtx)} />
          {f.hint && <span className={styles.hint}>{f.hint}</span>}
        </div>
      );
    }

    if (f.type === 'computed') {
      return (
        <div key={f.id} className={baseClass}>
          <label>{f.label}</label>
          <input type='text' readOnly value={computeField(f.compute, fields, f.sourceField)} />
        </div>
      );
    }

    const clientClass = [baseClass, styles.clientField].filter(Boolean).join(' ');

    if (f.type === 'select') {
      return (
        <div key={f.id} className={clientClass}>
          <label>
            {applyLabel(f.label)}
            {f.required && <span className={styles.req}>*</span>}
          </label>
          <select
            value={(fields[f.id] as string) ?? ''}
            onChange={(e) => setField(f.id, e.target.value)}
            disabled={isHost && false}
          >
            {f.options.map((o) => (
              <option key={o.value} value={o.value}>
                {applyLabel(o.label)}
              </option>
            ))}
          </select>
        </div>
      );
    }

    if (f.type === 'textarea') {
      return (
        <div key={f.id} className={clientClass}>
          <label>
            {applyLabel(f.label)}
            {f.required && <span className={styles.req}>*</span>}
          </label>
          <textarea
            rows={f.rows ?? 4}
            placeholder={f.placeholder}
            value={(fields[f.id] as string) ?? ''}
            onChange={(e) => setField(f.id, e.target.value)}
          />
        </div>
      );
    }

    if (f.type === 'date') {
      if (f.id === 'dob') {
        return (
          <DobField
            key={f.id}
            label={f.label}
            required={f.required}
            value={(fields[f.id] as string) ?? ''}
            onChange={(val) => setField(f.id, val)}
            applyLabel={applyLabel}
            invalid={invalid}
          />
        );
      }
      return (
        <DateInputField
          key={f.id}
          label={f.label}
          required={f.required}
          value={(fields[f.id] as string) ?? ''}
          onChange={(val) => setField(f.id, val)}
          applyLabel={applyLabel}
          invalid={invalid}
          defaultToToday={f.defaultToToday}
          lockField={f.lockField}
        />
      );
    }

    return (
      <div key={f.id} className={clientClass}>
        <label>
          {applyLabel(f.label)}
          {'required' in f && f.required && <span className={styles.req}>*</span>}
        </label>
        <input
          type={f.type}
          autoComplete='off'
          data-lpignore='true'
          data-form-type='other'
          placeholder={'placeholder' in f ? f.placeholder : undefined}
          value={(fields[f.id] as string) ?? ''}
          onChange={(e) => setField(f.id, e.target.value)}
        />
        {invalid && <span className={styles.err}>Required to sign this consent.</span>}
      </div>
    );
  };

  const renderBlock = (block: ContentBlock, blockIdx: number, sectionId: string) => {
    const blockId = `${sectionId}-${blockIdx}`;

    if (block.type === 'paragraph') {
      if (block.pdfOnly) return null;
      const paraStyle: React.CSSProperties = {
        ...(block.fontSize ? { fontSize: `${block.fontSize}px` } : {}),
        ...(block.color ? { color: block.color } : {}),
        ...(block.highlight ? { background: block.highlight } : {}),
        ...(block.indent ? { paddingLeft: `${32 + block.indent * 20}px` } : {}),
        ...(block.align ? { textAlign: block.align } : {}),
      };
      return block.bold ? (
        <p key={blockId} className={styles.subtitle} style={paraStyle}>
          {renderInlineText(block.text)}
        </p>
      ) : (
        <p key={blockId} className={styles.blockParagraph} style={paraStyle}>
          {renderInlineText(block.text)}
        </p>
      );
    }

    if (block.type === 'bullet-list') {
      const listFmtStyle: React.CSSProperties = {
        ...(block.fontSize ? { fontSize: `${block.fontSize}px` } : {}),
        ...(block.color ? { color: block.color } : {}),
        ...(block.highlight ? { background: block.highlight } : {}),
        ...(block.indent ? { paddingLeft: `${32 + block.indent * 20}px` } : {}),
      };
      if (block.listStyle === 'numbered') {
        return (
          <ol key={blockId} className={styles.blockList} style={{ listStyleType: 'decimal', ...listFmtStyle }}>
            {block.items.map((item, i) => (
              <li key={i}>{applyLabel(item)}</li>
            ))}
          </ol>
        );
      }
      if (block.listStyle === 'lettered') {
        const listStyleType = block.letterCase === 'upper' ? 'upper-alpha' : 'lower-alpha';
        return (
          <ol key={blockId} className={styles.blockList} style={{ listStyleType, ...listFmtStyle }}>
            {block.items.map((item, i) => (
              <li key={i}>{applyLabel(item)}</li>
            ))}
          </ol>
        );
      }
      if (block.listStyle === 'roman') {
        return (
          <ol key={blockId} className={styles.blockList} style={{ listStyleType: 'lower-roman', ...listFmtStyle }}>
            {block.items.map((item, i) => (
              <li key={i}>{applyLabel(item)}</li>
            ))}
          </ol>
        );
      }
      return (
        <ul key={blockId} className={styles.blockList} style={listFmtStyle}>
          {block.items.map((item, i) => (
            <li key={i}>{applyLabel(item)}</li>
          ))}
        </ul>
      );
    }

    if (block.type === 'callout') {
      return (
        <div key={blockId} className={`${styles.callout} ${styles.blockCalloutWrap}`}>
          {block.icon === 'warning' ? (
            <svg className={styles.ico} viewBox='0 0 24 24' fill='none' stroke='currentColor' strokeWidth='2'>
              <path d='M12 9v4' />
              <path d='M12 17h.01' />
              <path d='M10.3 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z' />
            </svg>
          ) : (
            <svg className={styles.ico} viewBox='0 0 24 24' fill='none' stroke='currentColor' strokeWidth='2'>
              <circle cx='12' cy='12' r='10' />
              <path d='M12 8v4' />
              <path d='M12 16h.01' />
            </svg>
          )}
          <div>
            <strong>{applyLabel(block.title)}</strong> {applyLabel(block.body)}
          </div>
        </div>
      );
    }

    if (block.type === 'field-group') {
      if (block.pdfOnly) return null;
      const gridClass = block.layout === '2-1' ? styles.fieldGrid21 : styles.fieldGrid;
      return (
        <div
          key={blockId}
          className={`${gridClass} ${styles.blockFieldGroup}`}
          style={block.fields.length === 1 ? { gridTemplateColumns: '1fr' } : undefined}
        >
          {block.fields.map(renderField)}
        </div>
      );
    }

    if (block.type === 'checklist') {
      const isRadio = block.variant === 'radio';
      return (
        <div key={blockId} className={`${styles.ackList} ${styles.blockChecklistWrap}`}>
          {block.label && (
            <div className={styles.scaleLabel}>
              {block.label}
              {block.required && <span className={styles.req}>*</span>}
            </div>
          )}
          {block.options.map((opt) => {
            const fieldId = `${block.id}:${opt.id}`;
            const checked = Boolean(fields[fieldId]);
            const handleSelect = () => {
              if (preview) return;
              if (isRadio && !block.multiSelect) {
                block.options.forEach((o) => setField(`${block.id}:${o.id}`, o.id === opt.id));
              } else {
                setField(fieldId, !checked);
              }
            };
            return (
              <div
                key={opt.id}
                className={`${styles.ack} ${checked ? styles.checked : ''} ${isRadio ? styles.ackRadio : ''}`}
                onClick={handleSelect}
                role={isRadio ? 'radio' : 'checkbox'}
                aria-checked={checked}
                tabIndex={preview ? -1 : 0}
                onKeyDown={(e) => {
                  if (e.key === ' ' || e.key === 'Enter') handleSelect();
                }}
              >
                <div className={isRadio ? styles.radio : styles.box} />
                <div className={styles.txt}>{applyLabel(opt.label)}</div>
              </div>
            );
          })}
        </div>
      );
    }

    if (block.type === 'signature') {
      return (
        <SignaturePad
          key={blockId}
          mode={sigMode}
          onModeChange={(m) => {
            setSigMode(m);
            setSigned(false);
            handleCommit({ kind: m, dataUrl: '' } as SignatureCommit);
          }}
          onChange={(hasSig) => {
            if (!hasSig) setSigned(false);
          }}
          onCommit={handleCommit}
          onApply={(dataUrl) => {
            setSigned(true);
            onApplySignature?.(dataUrl);
          }}
          disabled={preview || !!builderConfig}
          preview={preview || !!builderConfig}
          isHost={isHost}
          isMobilePreview={!!builderConfig?.isMobilePreview}
          externalSignature={isHost ? externalSignature ?? signature : undefined}
          clientLabel={clientLabel}
        />
      );
    }

    if (block.type === 'initials-pad') {
      const externalInitials: Record<string, string> = {};
      if (externalFieldState) {
        for (const [key, val] of Object.entries(externalFieldState)) {
          if (key.startsWith('__ip:') && typeof val === 'string' && val && val !== 'done') {
            externalInitials[key.slice(5)] = val;
          }
        }
      }
      return (
        <InitialsPad
          key={blockId}
          rows={block.rows}
          disabled={preview || !!builderConfig || isHost}
          preview={preview || !!builderConfig}
          sessionInitials={sessionInitials}
          externalInitials={externalInitials}
          onInitialsCaptured={setSessionInitials}
          onRowApplied={(rowId, val) => setField(`__ip:${rowId}`, val)}
        />
      );
    }

    if (block.type === 'address') {
      const enabledFields = block.subfields.filter((sf) => sf.enabled);
      // Group consecutive half-width fields into rows
      const rows: (typeof enabledFields)[] = [];
      let i = 0;
      while (i < enabledFields.length) {
        const sf = enabledFields[i];
        if (sf.halfWidth && i + 1 < enabledFields.length && enabledFields[i + 1].halfWidth) {
          rows.push([sf, enabledFields[i + 1]]);
          i += 2;
        } else {
          rows.push([sf]);
          i++;
        }
      }
      return (
        <div key={blockId} className={styles.addressBlock}>
          {rows.map((row, ri) => (
            <div key={ri} className={row.length === 2 ? styles.addressRow2 : styles.addressRow1}>
              {row.map((sf) => {
                const fieldId = `${block.id}:${sf.key}`;
                const invalid = showErr && sf.required && !fields[fieldId];
                if (sf.fieldType === 'select-country') {
                  return (
                    <div
                      key={sf.key}
                      className={[styles.field, styles.clientField, invalid ? styles.invalid : '']
                        .filter(Boolean)
                        .join(' ')}
                    >
                      <label>
                        {applyLabel(sf.label)}
                        {sf.required && <span className={styles.req}>*</span>}
                      </label>
                      <select
                        value={(fields[fieldId] as string) ?? 'US'}
                        onChange={(e) => setField(fieldId, e.target.value)}
                      >
                        {COUNTRIES.map((c) => (
                          <option key={c.value} value={c.value}>
                            {c.label}
                          </option>
                        ))}
                      </select>
                      {invalid && <span className={styles.err}>Required to sign this consent.</span>}
                    </div>
                  );
                }
                return (
                  <div
                    key={sf.key}
                    className={[styles.field, styles.clientField, invalid ? styles.invalid : '']
                      .filter(Boolean)
                      .join(' ')}
                  >
                    <label>
                      {applyLabel(sf.label)}
                      {sf.required && <span className={styles.req}>*</span>}
                    </label>
                    <input
                      type='text'
                      autoComplete='off'
                      data-lpignore='true'
                      data-form-type='other'
                      value={(fields[fieldId] as string) ?? ''}
                      onChange={(e) => setField(fieldId, e.target.value)}
                    />
                    {invalid && <span className={styles.err}>Required to sign this consent.</span>}
                  </div>
                );
              })}
            </div>
          ))}
        </div>
      );
    }

    if (block.type === 'print-name') {
      const nameId = `${block.id}:name`;
      const signingAsId = `${block.id}:signingAs`;
      const nameInvalid = showErr && block.nameRequired && !fields[nameId];
      const signingAsInvalid = showErr && block.signingAsRequired && !fields[signingAsId];
      const signingAsOptions = block.signingAsOptions || [];
      return (
        <div key={blockId} className={styles.addressBlock}>
          {/* Name — full width */}
          <div className={styles.addressRow1}>
            <div
              className={[styles.field, styles.clientField, nameInvalid ? styles.invalid : '']
                .filter(Boolean)
                .join(' ')}
            >
              <label>
                {block.nameLabel || 'Your Name (print):'}
                {block.nameRequired && <span className={styles.req}>*</span>}
              </label>
              <input
                type='text'
                placeholder={block.namePlaceholder || 'Print your name'}
                autoComplete='off'
                data-lpignore='true'
                data-form-type='other'
                value={(fields[nameId] as string) ?? ''}
                onChange={(e) => setField(nameId, e.target.value)}
              />
              {nameInvalid && <span className={styles.err}>Required to sign this consent.</span>}
            </div>
          </div>
          {/* Signing As — short width (occupies left half via 2-col grid with 1 child) */}
          <div className={styles.addressRow2}>
            <div
              className={[styles.field, styles.clientField, signingAsInvalid ? styles.invalid : '']
                .filter(Boolean)
                .join(' ')}
            >
              <label>
                {block.signingAsLabel || 'Signing as:'}
                {block.signingAsRequired && <span className={styles.req}>*</span>}
              </label>
              <select
                value={(fields[signingAsId] as string) ?? ''}
                onChange={(e) => setField(signingAsId, e.target.value)}
              >
                <option value=''>--- Select ---</option>
                {signingAsOptions.map((o) => (
                  <option key={o.value} value={o.value}>
                    {o.label}
                  </option>
                ))}
              </select>
              {signingAsInvalid && <span className={styles.err}>Required to sign this consent.</span>}
            </div>
          </div>
          {block.acknowledgment && <p className={styles.printNameAck}>{block.acknowledgment}</p>}
        </div>
      );
    }

    if (block.type === 'scale') {
      const scaleId = block.id;
      const currentVal = (fields[scaleId] as string) ?? '';
      const currentNum = parseFloat(currentVal);

      const setScaleVal = (v: number) => {
        if (preview || !!builderConfig) return;
        setField(scaleId, String(v));
      };

      const invalid = showErr && block.required && !currentVal;

      const EMOJI_5 = ['😢', '😕', '😐', '🙂', '😄'];
      const EMOJI_3 = ['😢', '😐', '😄'];

      // ── SVG emoji face sets ──────────────────────────────────────────────
      const renderEmojiFace = (idx: number): React.ReactNode => {
        const faces = [
          // 0 – no pain (green, big smile)
          <svg key={0} viewBox='5 5 90 90' width='100%' height='100%'>
            <circle cx='50' cy='50' r='45' fill='#E53935' />
            <circle cx='37' cy='42' r='4' fill='#1a1a1a' />
            <circle cx='63' cy='42' r='4' fill='#1a1a1a' />
            <path d='M37 70 Q50 59 63 70' stroke='#1a1a1a' strokeWidth='3.5' fill='none' strokeLinecap='round' />
            <ellipse cx='70' cy='57' rx='3' ry='5' fill='#90CAF9' />
          </svg>,
          // 1 – little pain (light green, slight smile)
          <svg key={1} viewBox='5 5 90 90' width='100%' height='100%'>
            <circle cx='50' cy='50' r='45' fill='#FF9800' />
            <circle cx='37' cy='42' r='4' fill='#1a1a1a' />
            <circle cx='63' cy='42' r='4' fill='#1a1a1a' />
            <path d='M37 70 Q50 59 63 70' stroke='#1a1a1a' strokeWidth='3.5' fill='none' strokeLinecap='round' />
          </svg>,
          // 2 – some pain (yellow, flat mouth)
          <svg key={2} viewBox='5 5 90 90' width='100%' height='100%'>
            <circle cx='50' cy='50' r='45' fill='#FDD835' />
            <circle cx='37' cy='42' r='4' fill='#1a1a1a' />
            <circle cx='63' cy='42' r='4' fill='#1a1a1a' />
            <line x1='36' y1='65' x2='64' y2='65' stroke='#1a1a1a' strokeWidth='3.5' strokeLinecap='round' />
          </svg>,
          // 3 – more pain (orange, frown)
          <svg key={3} viewBox='5 5 90 90' width='100%' height='100%'>
            <circle cx='50' cy='50' r='45' fill='#9CCC65' />
            <circle cx='37' cy='42' r='4' fill='#1a1a1a' />
            <circle cx='63' cy='42' r='4' fill='#1a1a1a' />
            <path d='M37 63 Q50 72 63 63' stroke='#1a1a1a' strokeWidth='3.5' fill='none' strokeLinecap='round' />
          </svg>,
          // 4 – worst pain (red, frown + teardrop)
          <svg key={4} viewBox='5 5 90 90' width='100%' height='100%'>
            <circle cx='50' cy='50' r='45' fill='#66BB6A' />
            <circle cx='37' cy='42' r='4' fill='#1a1a1a' />
            <circle cx='63' cy='42' r='4' fill='#1a1a1a' />
            <path d='M33 60 Q50 78 67 60' stroke='#1a1a1a' strokeWidth='3.5' fill='none' strokeLinecap='round' />
          </svg>,
        ];
        return faces[idx] ?? faces[0];
      };

      const renderColorFace = (idx: number): React.ReactNode => {
        const colorSvgs = [
          // 0 – very sad (colors-01)
          <svg key={0} viewBox='8 8 144 144' width='100%' height='100%'>
            <defs>
              <radialGradient id='cf0bg' cx='50%' cy='45%' r='55%'>
                <stop offset='0%' stopColor='#FFE066' />
                <stop offset='100%' stopColor='#FFB300' />
              </radialGradient>
            </defs>
            <circle cx='80' cy='80' r='72' fill='url(#cf0bg)' />
            <ellipse cx='57' cy='68' rx='9' ry='11' fill='#4A3728' />
            <ellipse cx='103' cy='68' rx='9' ry='11' fill='#4A3728' />
            <path d='M52 108 Q80 88 108 108' stroke='#4A3728' strokeWidth='4' fill='none' strokeLinecap='round' />
            <ellipse cx='112' cy='85' rx='4' ry='7' fill='#64B5F6' opacity='0.85' />
          </svg>,
          // 1 – sad (colors-02)
          <svg key={1} viewBox='8 8 144 144' width='100%' height='100%'>
            <defs>
              <radialGradient id='cf1bg' cx='50%' cy='45%' r='55%'>
                <stop offset='0%' stopColor='#FFE066' />
                <stop offset='100%' stopColor='#FFB300' />
              </radialGradient>
            </defs>
            <circle cx='80' cy='80' r='72' fill='url(#cf1bg)' />
            <ellipse cx='57' cy='70' rx='9' ry='11' fill='#4A3728' />
            <ellipse cx='103' cy='70' rx='9' ry='11' fill='#4A3728' />
            <path d='M55 105 Q80 93 105 105' stroke='#4A3728' strokeWidth='4' fill='none' strokeLinecap='round' />
          </svg>,
          // 2 – neutral (colors-03)
          <svg key={2} viewBox='8 8 144 144' width='100%' height='100%'>
            <defs>
              <radialGradient id='cf2bg' cx='50%' cy='45%' r='55%'>
                <stop offset='0%' stopColor='#FFE066' />
                <stop offset='100%' stopColor='#FFB300' />
              </radialGradient>
            </defs>
            <circle cx='80' cy='80' r='72' fill='url(#cf2bg)' />
            <ellipse cx='57' cy='70' rx='9' ry='11' fill='#4A3728' />
            <ellipse cx='103' cy='70' rx='9' ry='11' fill='#4A3728' />
            <line x1='55' y1='104' x2='105' y2='104' stroke='#4A3728' strokeWidth='4' strokeLinecap='round' />
          </svg>,
          // 3 – happy (colors-04)
          <svg key={3} viewBox='8 8 144 144' width='100%' height='100%'>
            <defs>
              <radialGradient id='cf3bg' cx='50%' cy='45%' r='55%'>
                <stop offset='0%' stopColor='#FFE580' />
                <stop offset='100%' stopColor='#FFB300' />
              </radialGradient>
            </defs>
            <circle cx='80' cy='80' r='72' fill='url(#cf3bg)' />
            <ellipse cx='57' cy='70' rx='9' ry='11' fill='#4A3728' />
            <ellipse cx='103' cy='70' rx='9' ry='11' fill='#4A3728' />
            <path d='M55 97 Q80 116 105 97' stroke='#4A3728' strokeWidth='4' fill='none' strokeLinecap='round' />
          </svg>,
          // 4 – very happy (colors-05): squinting arc eyes + open grin with teeth
          <svg key={4} viewBox='8 8 144 144' width='100%' height='100%'>
            <defs>
              <radialGradient id='cf4bg' cx='50%' cy='45%' r='55%'>
                <stop offset='0%' stopColor='#FFE580' />
                <stop offset='100%' stopColor='#FFA000' />
              </radialGradient>
            </defs>
            <circle cx='80' cy='80' r='72' fill='url(#cf4bg)' />
            <path d='M50 72 Q57 61 64 72' stroke='#4A3728' strokeWidth='5' fill='none' strokeLinecap='round' />
            <path d='M96 72 Q103 61 110 72' stroke='#4A3728' strokeWidth='5' fill='none' strokeLinecap='round' />
            <path d='M44 94 Q80 138 116 94 Q80 116 44 94 Z' fill='#4A3728' />
            <path d='M48 96 Q80 126 112 96 Q80 113 48 96 Z' fill='white' />
          </svg>,
        ];
        return colorSvgs[idx] ?? colorSvgs[0];
      };

      const renderAnimalFace = (idx: number): React.ReactNode => {
        // idx 0=worst(puppy) … idx 4=best(bunny)
        if (idx === 0) {
          return (
            <svg viewBox='460 462 155 125' preserveAspectRatio='xMidYMax meet' width='100%' height='100%'>
              <path
                fill='#B71C1C'
                d='M475.05,502.274c6.977-18.455,22.496-30.977,34.664-27.967c12.169,3.011,16.378,20.409,9.402,38.864c-6.978,18.456-22.497,30.977-34.665,27.966C472.282,538.129,468.073,520.729,475.05,502.274z'
              />
              <path
                fill='#B71C1C'
                d='M560.886,512.365c-6.977-18.454-2.768-35.854,9.4-38.864c12.168-3.009,27.688,9.512,34.664,27.967c6.977,18.454,2.768,35.854-9.4,38.864S567.862,530.82,560.886,512.365z'
              />
              <circle fill='#E53935' cx='540' cy='525.6' r='54.72' />
              <ellipse fill='#C62828' cx='540' cy='545.76' rx='23.04' ry='15.84' />
              <ellipse fill='#C62828' cx='519.15' cy='508.32' rx='19.35' ry='15.84' />
              <ellipse fill='#333333' cx='540' cy='535.68' rx='10.08' ry='7.2' />
              <circle fill='#333333' cx='519.84' cy='508.32' r='7.2' />
              <circle fill='#333333' cx='560.16' cy='508.32' r='7.2' />
              <path
                fill='none'
                stroke='#FFFFFF'
                strokeWidth='3.5'
                strokeLinecap='round'
                d='M514.08,560.16c17.28-9.603,34.56-9.603,51.84,0'
              />
              <path
                fill='#64B5F6'
                d='M567.332,519.307c-5.302,8.193-5.153,19.02,0.26,18.946C573.006,538.179,572.856,527.353,567.332,519.307z'
              />
            </svg>
          );
        }
        if (idx === 1) {
          return (
            <svg viewBox='12 6 76 92' preserveAspectRatio='xMidYMax meet' width='100%' height='100%'>
              <polygon points='22,32 30,8 46,36' fill='#FF9800' />
              <polygon points='25,30 30,12 42,34' fill='#ffcc80' />
              <polygon points='54,36 70,8 78,32' fill='#FF9800' />
              <polygon points='58,34 70,12 75,30' fill='#ffcc80' />
              <circle cx='50' cy='58' r='38' fill='#FF9800' />
              <circle cx='37' cy='50' r='5' fill='#333' />
              <circle cx='63' cy='50' r='5' fill='#333' />
              <line
                x1='14'
                y1='60'
                x2='34'
                y2='62'
                stroke='#333'
                strokeWidth='1.5'
                strokeLinecap='round'
                opacity='0.6'
              />
              <line
                x1='14'
                y1='67'
                x2='34'
                y2='65'
                stroke='#333'
                strokeWidth='1.5'
                strokeLinecap='round'
                opacity='0.6'
              />
              <line
                x1='66'
                y1='62'
                x2='86'
                y2='60'
                stroke='#333'
                strokeWidth='1.5'
                strokeLinecap='round'
                opacity='0.6'
              />
              <line
                x1='66'
                y1='65'
                x2='86'
                y2='67'
                stroke='#333'
                strokeWidth='1.5'
                strokeLinecap='round'
                opacity='0.6'
              />
              <polygon points='50,63 47,68 53,68' fill='#e64a19' />
              <path d='M 32 78 Q 50 68 68 78' stroke='#333' strokeWidth='3.5' fill='none' strokeLinecap='round' />
            </svg>
          );
        }
        if (idx === 2) {
          return (
            <svg viewBox='10 -5 80 105' preserveAspectRatio='xMidYMax meet' width='100%' height='100%'>
              <line x1='40' y1='24' x2='32' y2='4' stroke='#FDD835' strokeWidth='3' strokeLinecap='round' />
              <circle cx='32' cy='2' r='5' fill='#FDD835' stroke='#c8a800' strokeWidth='1.5' />
              <line x1='60' y1='24' x2='68' y2='4' stroke='#FDD835' strokeWidth='3' strokeLinecap='round' />
              <circle cx='68' cy='2' r='5' fill='#FDD835' stroke='#c8a800' strokeWidth='1.5' />
              <circle cx='50' cy='60' r='38' fill='#FDD835' />
              <ellipse cx='37' cy='54' rx='7' ry='9' fill='#333' />
              <ellipse cx='63' cy='54' rx='7' ry='9' fill='#333' />
              <ellipse cx='35' cy='52' rx='2.5' ry='3' fill='#fff' opacity='0.4' />
              <ellipse cx='61' cy='52' rx='2.5' ry='3' fill='#fff' opacity='0.4' />
              <line x1='36' y1='70' x2='64' y2='70' stroke='#333' strokeWidth='3' strokeLinecap='round' />
            </svg>
          );
        }
        if (idx === 3) {
          return (
            <svg viewBox='10 12 80 88' preserveAspectRatio='xMidYMax meet' width='100%' height='100%'>
              <circle cx='28' cy='28' r='14' fill='#9CCC65' />
              <circle cx='72' cy='28' r='14' fill='#9CCC65' />
              <circle cx='28' cy='28' r='8' fill='#c5e1a5' />
              <circle cx='72' cy='28' r='8' fill='#c5e1a5' />
              <circle cx='50' cy='58' r='38' fill='#9CCC65' />
              <ellipse cx='50' cy='68' rx='16' ry='11' fill='#c5e1a5' />
              <circle cx='37' cy='50' r='5' fill='#333' />
              <circle cx='63' cy='50' r='5' fill='#333' />
              <path d='M 40 70 Q 50 78 60 70' stroke='#333' strokeWidth='3' fill='none' strokeLinecap='round' />
            </svg>
          );
        }
        return (
          <svg viewBox='10 -2 80 102' preserveAspectRatio='xMidYMax meet' width='100%' height='100%'>
            <ellipse cx='36' cy='18' rx='8' ry='18' fill='#66BB6A' />
            <ellipse cx='36' cy='18' rx='4' ry='13' fill='#a5d6a7' />
            <ellipse cx='64' cy='18' rx='8' ry='18' fill='#66BB6A' />
            <ellipse cx='64' cy='18' rx='4' ry='13' fill='#a5d6a7' />
            <circle cx='50' cy='60' r='38' fill='#66BB6A' />
            <circle cx='37' cy='52' r='5' fill='#333' />
            <circle cx='63' cy='52' r='5' fill='#333' />
            <path d='M 30 66 Q 50 86 70 66' stroke='#333' strokeWidth='3.5' fill='none' strokeLinecap='round' />
            <circle cx='28' cy='64' r='7' fill='#ff8a80' opacity='0.5' />
            <circle cx='72' cy='64' r='7' fill='#ff8a80' opacity='0.5' />
            <ellipse cx='50' cy='59' rx='3' ry='2' fill='#ff8a80' />
          </svg>
        );
      };

      const renderCloudFace = (idx: number): React.ReactNode => {
        const cloudData = [
          { fill: '#3D3D4D', stroke: '#D0D0D0', lightning: true, rain: 2 },
          { fill: '#6B6B7B', stroke: '#D0D0D0', lightning: false, rain: 2 },
          { fill: '#B0B0C0', stroke: '#333', lightning: false, rain: 5 },
          { fill: '#E8E8F0', stroke: '#333', lightning: false, rain: 3 },
          { fill: '#FFD700', stroke: '#333', lightning: false, rain: 0 },
        ];
        const d = cloudData[idx];
        const isSunshine = idx === 4;
        // Crop viewBox tightly around each face type
        const vb = isSunshine ? '15 15 130 130' : d.rain > 0 ? '44 55 72 80' : '44 55 72 50';
        return (
          <svg viewBox={vb} width='100%' height='100%'>
            {isSunshine ? (
              <>
                <circle cx='80' cy='80' r='28' fill='#FFD700' opacity='0.3' />
                {[0, 45, 90, 135, 180, 225, 270, 315].map((angle) => {
                  const rad = (angle * Math.PI) / 180;
                  return (
                    <line
                      key={angle}
                      x1={80 + Math.cos(rad) * 35}
                      y1={80 + Math.sin(rad) * 35}
                      x2={80 + Math.cos(rad) * 48}
                      y2={80 + Math.sin(rad) * 48}
                      stroke='#FFD700'
                      strokeWidth='3'
                      strokeLinecap='round'
                    />
                  );
                })}
                <circle cx='80' cy='80' r='25' fill='#FFD700' />
                <circle cx='70' cy='72' r='3' fill='#333' />
                <circle cx='90' cy='72' r='3' fill='#333' />
                <path d='M 68 84 Q 80 94 92 84' stroke='#333' strokeWidth='2.5' fill='none' strokeLinecap='round' />
              </>
            ) : (
              <>
                <ellipse cx='65' cy='80' rx='12' ry='14' fill={d.fill} />
                <ellipse cx='80' cy='75' rx='16' ry='16' fill={d.fill} />
                <ellipse cx='95' cy='80' rx='12' ry='14' fill={d.fill} />
                <rect x='56' y='80' width='48' height='12' fill={d.fill} />
                {idx <= 1 ? (
                  <>
                    <path
                      d='M 68 77 Q 72 79 76 77'
                      stroke={d.stroke}
                      strokeWidth='2.5'
                      fill='none'
                      strokeLinecap='round'
                    />
                    <path
                      d='M 84 77 Q 88 79 92 77'
                      stroke={d.stroke}
                      strokeWidth='2.5'
                      fill='none'
                      strokeLinecap='round'
                    />
                    <path
                      d='M 70 86 Q 80 81 90 86'
                      stroke={d.stroke}
                      strokeWidth='3'
                      fill='none'
                      strokeLinecap='round'
                    />
                  </>
                ) : (
                  <>
                    <circle cx='72' cy='78' r='3' fill={idx === 2 ? '#E0E0E0' : '#E0E0E0'} />
                    <circle cx='88' cy='78' r='3' fill={idx === 2 ? '#E0E0E0' : '#E0E0E0'} />
                    <circle cx='72' cy='78' r='1.5' fill='#333' />
                    <circle cx='88' cy='78' r='1.5' fill='#333' />
                    {idx === 2 && (
                      <path d='M 74 84 Q 80 82 86 84' stroke='#333' strokeWidth='2' fill='none' strokeLinecap='round' />
                    )}
                    {idx === 3 && (
                      <path d='M 74 83 Q 80 86 86 83' stroke='#333' strokeWidth='2' fill='none' strokeLinecap='round' />
                    )}
                  </>
                )}
                {d.lightning && (
                  <path
                    d='M 80 95 L 84 102 L 80 110 L 84 118'
                    stroke='#FFD700'
                    strokeWidth='2.5'
                    fill='none'
                    strokeLinecap='round'
                    strokeLinejoin='round'
                  />
                )}
                {Array.from({ length: d.rain }).map((_, ri) => {
                  const rainXs = d.rain === 2 ? [72, 92] : d.rain === 3 ? [65, 80, 95] : [60, 70, 80, 90, 100];
                  return (
                    <line
                      key={ri}
                      x1={rainXs[ri]}
                      y1='95'
                      x2={rainXs[ri]}
                      y2='125'
                      stroke='#4A90E2'
                      strokeWidth={d.rain <= 2 ? 3 : 2.5}
                      strokeLinecap='round'
                    />
                  );
                })}
              </>
            )}
          </svg>
        );
      };

      const renderShapeFace = (idx: number): React.ReactNode => {
        if (idx === 0) {
          return (
            <svg viewBox='0 0 200 200' width='100%' height='100%'>
              <path
                fill='#E57373'
                d='M180.966,81.204l-24.578-43.375c-6.748-11.951-16.387-17.928-28.916-17.928H72.528c-12.529,0-22.168,5.977-28.917,17.928L19.034,81.204c-7.711,12.531-7.711,25.062,0,37.592l24.578,43.375c6.749,11.953,16.388,17.928,28.917,17.928h54.943c12.529,0,22.168-5.975,28.916-17.928l24.578-43.375C188.678,106.266,188.678,93.735,180.966,81.204z'
              />
              <ellipse fill='#0A0A0A' cx='75' cy='85' rx='4.5' ry='7' />
              <ellipse fill='#0A0A0A' cx='125' cy='85' rx='4.5' ry='7' />
              <path
                fill='none'
                stroke='#0A0A0A'
                strokeWidth='6'
                strokeLinecap='round'
                d='M75,135c16.667-16.666,33.334-16.666,50,0'
              />
              <path
                fill='#64B5F6'
                d='M129.871,98.382c-4.757,15.475,2.216,33.11,11.621,29.391C150.897,124.055,143.925,106.42,129.871,98.382z'
              />
            </svg>
          );
        }
        const shapes = [
          { fill: '#FF8A3D', shape: 'rect', eyes: true, mouthSad: true },
          { fill: '#FFD42A', shape: 'circle', eyes: false, neutral: true },
          {
            fill: '#5BD5C9',
            path: 'M100 18 L123 78 L188 82 L137 122 L155 184 L100 148 L45 184 L63 122 L12 82 L77 78 Z',
            eyes: true,
            smile: true,
          },
          {
            fill: '#3FBF5A',
            path: 'M100 175 C 60 145, 20 115, 20 75 C 20 50, 40 30, 65 30 C 82 30, 95 40, 100 55 C 105 40, 118 30, 135 30 C 160 30, 180 50, 180 75 C 180 115, 140 145, 100 175 Z',
            eyes: false,
            heartSmile: true,
          },
        ];
        const s = shapes[idx - 1];
        return (
          <svg viewBox='0 0 200 200' width='100%' height='100%'>
            {s.path ? (
              <path d={s.path} fill={s.fill} />
            ) : s.shape === 'rect' ? (
              <rect x='20' y='20' width='160' height='160' rx='32' ry='32' fill={s.fill} />
            ) : (
              <circle cx='100' cy='100' r='80' fill={s.fill} />
            )}
            {idx === 1 && (
              <>
                <ellipse cx='75' cy='85' rx='4.5' ry='7' fill='#0a0a0a' />
                <ellipse cx='125' cy='85' rx='4.5' ry='7' fill='#0a0a0a' />
                <path
                  d='M75 135 Q 100 110, 125 135'
                  fill='none'
                  stroke='#0a0a0a'
                  strokeWidth='6'
                  strokeLinecap='round'
                />
              </>
            )}
            {idx === 2 && (
              <>
                <ellipse cx='75' cy='88' rx='4.5' ry='7' fill='#0a0a0a' />
                <ellipse cx='125' cy='88' rx='4.5' ry='7' fill='#0a0a0a' />
                <line x1='75' y1='125' x2='125' y2='125' stroke='#0a0a0a' strokeWidth='6' strokeLinecap='round' />
              </>
            )}
            {idx === 3 && (
              <>
                <path d='M68 92 Q 78 80, 88 92' fill='none' stroke='#0a0a0a' strokeWidth='5' strokeLinecap='round' />
                <path d='M112 92 Q 122 80, 132 92' fill='none' stroke='#0a0a0a' strokeWidth='5' strokeLinecap='round' />
                <path
                  d='M72 115 Q 100 145, 128 115'
                  fill='none'
                  stroke='#0a0a0a'
                  strokeWidth='6'
                  strokeLinecap='round'
                />
              </>
            )}
            {idx === 4 && (
              <>
                <path d='M55 78 Q 65 65, 75 78' fill='none' stroke='#0a0a0a' strokeWidth='6' strokeLinecap='round' />
                <path d='M125 78 Q 135 65, 145 78' fill='none' stroke='#0a0a0a' strokeWidth='6' strokeLinecap='round' />
                <path d='M70 100 Q 100 145, 130 100 Q 100 115, 70 100 Z' fill='#0a0a0a' />
              </>
            )}
          </svg>
        );
      };

      const emojiType = (block as { emojiType?: string }).emojiType ?? 'emoji';
      const emojiLabels: string[] =
        (block as { emojiLabels?: string[] }).emojiLabels ??
        (block.emojiCount === 3 ? ['Sad', 'Neutral', 'Happy'] : ['Very Sad', 'Sad', 'Neutral', 'Happy', 'Very Happy']);
      const likertOptions: string[] = (block as { likertOptions?: string[] }).likertOptions ?? [
        'Strongly\nDisagree',
        'Disagree',
        'Neutral',
        'Agree',
        'Strongly\nAgree',
      ];

      const endLabels = (
        <div className={styles.scaleEndLabels}>
          <span className={styles.scaleEndLeft}>{block.leftLabel}</span>
          <span className={styles.scaleEndRight}>{block.rightLabel}</span>
        </div>
      );

      const endLikertLabels = (
        <div className={styles.scaleLikertEndLabels}>
          <span className={styles.scaleEndLeft}>{block.leftLabel}</span>
          <span className={styles.scaleEndRight}>{block.rightLabel}</span>
        </div>
      );

      let scaleControl: React.ReactNode = null;

      if (block.scaleType === 'numeric') {
        const steps: number[] = [];
        for (let n = block.min; n <= block.max; n += block.step) {
          steps.push(Math.round(n * 100) / 100);
        }
        scaleControl = (
          <>
            <div
              className={[styles.scaleNumeric, block.orientation === 'vertical' ? styles.scaleVertical : '']
                .filter(Boolean)
                .join(' ')}
            >
              {steps.map((n) => (
                <button
                  key={n}
                  className={[styles.scaleNumBtn, n === currentNum ? styles.scaleNumBtnActive : '']
                    .filter(Boolean)
                    .join(' ')}
                  onClick={() => setScaleVal(n)}
                  type='button'
                >
                  {n}
                </button>
              ))}
            </div>
            {(block.leftLabel || block.rightLabel) && endLikertLabels}
          </>
        );
      } else if (block.scaleType === 'emoji') {
        const unicodeEmojis = block.emojiCount === 3 ? EMOJI_3 : EMOJI_5;
        const total = unicodeEmojis.length;
        scaleControl = (
          <div className={styles.scaleEmoji}>
            {unicodeEmojis.map((emoji, idx) => {
              const val = block.min + ((block.max - block.min) / (total - 1)) * idx;
              const rounded = Math.round(val * 10) / 10;
              const isActive = rounded === currentNum;
              const label = emojiLabels[idx] ?? '';
              return (
                <div
                  key={idx}
                  className={[styles.scaleEmojiItem, isActive ? styles.scaleEmojiItemActive : '']
                    .filter(Boolean)
                    .join(' ')}
                  onClick={() => setScaleVal(rounded)}
                  role='button'
                  tabIndex={0}
                  onKeyDown={(e) => e.key === 'Enter' && setScaleVal(rounded)}
                >
                  {emojiType === 'emoji' && <span className={styles.scaleEmojiSvg}>{renderColorFace(idx)}</span>}
                  {emojiType === 'colors' && <span className={styles.scaleEmojiSvg}>{renderEmojiFace(idx)}</span>}
                  {emojiType === 'animals' && <span className={styles.scaleEmojiSvg}>{renderAnimalFace(idx)}</span>}
                  {emojiType === 'clouds' && <span className={styles.scaleEmojiSvg}>{renderCloudFace(idx)}</span>}
                  {emojiType === 'shapes' && <span className={styles.scaleEmojiSvg}>{renderShapeFace(idx)}</span>}
                  {label && <span className={styles.scaleEmojiLabel}>{label}</span>}
                </div>
              );
            })}
          </div>
        );
      } else if (block.scaleType === 'stars') {
        const count = block.max - block.min + 1;
        const stars = Array.from({ length: count }, (_, i) => block.min + i);
        scaleControl = (
          <div className={styles.scaleControlWrapInline}>
            <div className={styles.scaleStars}>
              {stars.map((n) => {
                const filled = currentNum >= n;
                const halfFilled = block.allowHalf && currentNum >= n - 0.5 && currentNum < n;
                return (
                  <button
                    key={n}
                    className={[
                      styles.scaleStar,
                      filled ? styles.scaleStarFilled : halfFilled ? styles.scaleStarHalf : '',
                    ]
                      .filter(Boolean)
                      .join(' ')}
                    onClick={() => setScaleVal(n)}
                    onMouseEnter={(e) => {
                      if (block.allowHalf) {
                        const rect = (e.currentTarget as HTMLButtonElement).getBoundingClientRect();
                        const isLeft = e.clientX - rect.left < rect.width / 2;
                        setScaleVal(isLeft ? n - 0.5 : n);
                      }
                    }}
                    type='button'
                  >
                    ★
                  </button>
                );
              })}
            </div>
            {(block.leftLabel || block.rightLabel) && (
              <div className={`${styles.scaleEndLabels} ${styles.scaleStarsEndLabels}`}>
                <span className={styles.scaleEndLeft}>{block.leftLabel}</span>
                <span className={styles.scaleEndRight}>{block.rightLabel}</span>
              </div>
            )}
          </div>
        );
      } else if (block.scaleType === 'slider') {
        scaleControl = (
          <div className={styles.scaleSliderWrap}>
            <div className={styles.scaleSlider}>
              <input
                type='range'
                min={block.min}
                max={block.max}
                step={block.allowHalf ? 0.5 : block.step}
                value={currentNum || block.min}
                disabled={preview || !!builderConfig}
                onChange={(e) => setScaleVal(parseFloat(e.target.value))}
                className={styles.scaleRangeInput}
              />
              {currentVal && <span className={styles.scaleSliderVal}>{currentNum}</span>}
            </div>
            {(block.leftLabel || block.rightLabel) && endLabels}
          </div>
        );
      } else if (block.scaleType === 'likert') {
        scaleControl = (
          <div className={styles.scaleControlWrap}>
            <div
              className={[styles.scaleLikert, block.orientation === 'vertical' ? styles.scaleVertical : '']
                .filter(Boolean)
                .join(' ')}
              style={
                block.orientation !== 'vertical'
                  ? { gridTemplateColumns: `repeat(${likertOptions.length}, 1fr)` }
                  : undefined
              }
            >
              {likertOptions.map((lbl, idx) => {
                const n = block.min + idx;
                return (
                  <button
                    key={n}
                    className={[styles.scaleLikertBtn, n === currentNum ? styles.scaleLikertBtnActive : '']
                      .filter(Boolean)
                      .join(' ')}
                    onClick={() => setScaleVal(n)}
                    type='button'
                  >
                    {lbl.split('\n').map((part, pi) => (
                      <span key={pi} style={{ display: 'block' }}>
                        {part}
                      </span>
                    ))}
                  </button>
                );
              })}
            </div>
          </div>
        );
      }

      return (
        <div key={blockId} className={[styles.scaleBlock, invalid ? styles.invalid : ''].filter(Boolean).join(' ')}>
          {block.label && (
            <div className={styles.scaleLabel}>
              {applyLabel(block.label)}
              {block.required && <span className={styles.req}>*</span>}
            </div>
          )}
          {scaleControl}
          {invalid && <span className={styles.err}>Required to sign this consent.</span>}
        </div>
      );
    }

    if (block.type === 'time-field') {
      const tf = block as typeof block & {
        type: 'time-field';
        id: string;
        label: string;
        required: boolean;
        use24hr: boolean;
      };
      const timeVal = (fields[tf.id] as string) ?? '';
      const invalid = showErr && tf.required && !timeVal;

      const parseTime = (val: string) => {
        const m = val.match(/^(\d+):(\d+)(?:\s*(AM|PM))?$/i);
        if (!m) return { hour: '', minute: '', ampm: 'AM' };
        return { hour: m[1], minute: m[2], ampm: (m[3] ?? 'AM').toUpperCase() };
      };

      const { hour, minute, ampm } = parseTime(timeVal);

      const setTime = (h: string, m: string, ap: string) => {
        if (preview || !!builderConfig) return;
        const val = tf.use24hr ? `${h}:${m}` : `${h}:${m} ${ap}`;
        setField(tf.id, val);
      };

      const hourOptions = tf.use24hr
        ? Array.from({ length: 24 }, (_, i) => String(i).padStart(2, '0'))
        : Array.from({ length: 12 }, (_, i) => String(i + 1));
      const minuteOptions = Array.from({ length: 60 }, (_, i) => String(i).padStart(2, '0'));

      return (
        <div key={blockId} className={[styles.timeField, invalid ? styles.invalid : ''].filter(Boolean).join(' ')}>
          {tf.label && (
            <label className={styles.timeLabel}>
              {tf.label}
              {tf.required && <span className={styles.req}>*</span>}
            </label>
          )}
          <div className={styles.timeInputRow}>
            <select
              className={styles.timeSelect}
              value={hour}
              disabled={preview || !!builderConfig}
              onChange={(e) => setTime(e.target.value, minute || '00', ampm)}
            >
              <option value=''>HH</option>
              {hourOptions.map((h) => (
                <option key={h} value={h}>
                  {h}
                </option>
              ))}
            </select>
            <span className={styles.timeSep}>:</span>
            <select
              className={styles.timeSelect}
              value={minute}
              disabled={preview || !!builderConfig}
              onChange={(e) => setTime(hour || (tf.use24hr ? '0' : '1'), e.target.value, ampm)}
            >
              <option value=''>MM</option>
              {minuteOptions.map((m) => (
                <option key={m} value={m}>
                  {m}
                </option>
              ))}
            </select>
            {!tf.use24hr && (
              <select
                className={styles.timeSelect}
                value={ampm}
                disabled={preview || !!builderConfig}
                onChange={(e) => setTime(hour || '1', minute || '00', e.target.value)}
              >
                <option value='AM'>AM</option>
                <option value='PM'>PM</option>
              </select>
            )}
          </div>
          {invalid && <span className={styles.err}>Required to sign this consent.</span>}
        </div>
      );
    }

    if (block.type === 'matrix') {
      const mx = block as typeof block & {
        type: 'matrix';
        id: string;
        label?: string;
        columns: string[];
        rows: { id: string; text: string }[];
        required: boolean;
      };
      const anyInvalid = showErr && mx.required && mx.rows.some((r) => !fields[`${mx.id}:${r.id}`]);
      return (
        <div
          key={blockId}
          className={[styles.matrixBlock, anyInvalid ? styles.matrixInvalid : ''].filter(Boolean).join(' ')}
        >
          {mx.label && (
            <div className={styles.matrixLabel}>
              {mx.label}
              {mx.required && <span className={styles.req}>*</span>}
            </div>
          )}
          <table className={styles.matrixTable}>
            <thead>
              <tr>
                <th className={styles.matrixHeaderEmpty} />
                {mx.columns.map((col, ci) => (
                  <th key={ci} className={styles.matrixColHeader}>
                    {col}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {mx.rows.map((row) => {
                const fieldId = `${mx.id}:${row.id}`;
                const selectedCol = fields[fieldId] as string | undefined;
                return (
                  <tr key={row.id}>
                    <td className={styles.matrixRowText}>{row.text}</td>
                    {mx.columns.map((col, ci) => (
                      <td key={ci} className={styles.matrixRadioCell}>
                        <input
                          type='radio'
                          name={fieldId}
                          value={col}
                          checked={selectedCol === col}
                          onChange={() => {
                            if (preview || !!builderConfig) return;
                            setField(fieldId, col);
                          }}
                        />
                      </td>
                    ))}
                  </tr>
                );
              })}
            </tbody>
          </table>
          {anyInvalid && <span className={styles.err}>Required to sign this consent.</span>}
        </div>
      );
    }

    if (block.type === 'office-use') {
      if (!builderConfig) return null; // live form: hidden for all; host sees it in PDF only
      const label = block.label || 'For Office Use';
      return (
        <div key={blockId} className={styles.officeUseBlock}>
          <div className={styles.officeUseBlockLabel}>{label}</div>
          <div className={styles.officeUseInner}>
            {block.layoutType === 'table' ? (
              <table className={styles.officeUseTable}>
                <thead>
                  <tr>
                    {block.columns.map((col) => (
                      <th key={col.id} style={col.width ? { width: `${col.width}%` } : undefined}>
                        {col.label}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    {block.columns.map((col) => (
                      <td key={col.id} style={col.width ? { width: `${col.width}%` } : undefined} />
                    ))}
                  </tr>
                </tbody>
              </table>
            ) : (
              <div className={styles.officeUseSingleRow}>
                {block.columns.map((col) => (
                  <div
                    key={col.id}
                    className={styles.officeUseSingleCell}
                    style={col.width ? { flex: `0 0 ${col.width}%` } : undefined}
                  >
                    <span className={styles.officeUseCellLabel}>{col.label}</span>
                  </div>
                ))}
              </div>
            )}
          </div>
          {!!builderConfig && (
            <div className={styles.officeUsePdfPill}>This field is for office use only (not included in PDFs)</div>
          )}
        </div>
      );
    }

    if (block.type === 'image') {
      const imgMarginStyle = {
        marginTop: block.marginTop ?? 15,
        marginBottom: block.marginBottom ?? 15,
      };
      if (!block.src) {
        return (
          <div key={blockId} className={styles.mediaPlaceholder} style={imgMarginStyle}>
            Image URL not set
          </div>
        );
      }
      return (
        <div key={blockId} style={{ textAlign: block.alignment ?? 'center', ...imgMarginStyle }}>
          <img
            src={block.src}
            alt={block.alt ?? ''}
            style={{
              width: `${block.widthPercent ?? 100}%`,
              maxWidth: '100%',
              ...(block.maxHeight ? { maxHeight: block.maxHeight, objectFit: 'contain' as const } : {}),
            }}
          />
          {block.alt && <p className={styles.mediaCaption}>{block.alt}</p>}
        </div>
      );
    }

    if (block.type === 'video') {
      const embedUrl = (() => {
        try {
          const url = new URL(block.src);
          const host = url.hostname.replace(/^www\./, '');
          if (host === 'youtube.com') {
            if (url.searchParams.get('v')) return `https://www.youtube.com/embed/${url.searchParams.get('v')}`;
            const shortsMatch = url.pathname.match(/^\/shorts\/([^/?]+)/);
            if (shortsMatch) return `https://www.youtube.com/embed/${shortsMatch[1]}`;
            return null;
          }
          if (host === 'youtu.be') {
            const id = url.pathname.slice(1);
            return id ? `https://www.youtube.com/embed/${id}` : null;
          }
          if (host === 'vimeo.com') {
            const id = url.pathname.replace(/^\//, '').split('/')[0];
            return id ? `https://player.vimeo.com/video/${id}` : null;
          }
          return null;
        } catch {
          return null;
        }
      })();
      const vidMarginStyle = {
        marginTop: block.marginTop ?? 15,
        marginBottom: block.marginBottom ?? 15,
      };
      if (!embedUrl) {
        return (
          <div key={blockId} className={styles.mediaPlaceholder} style={vidMarginStyle}>
            {block.src ? 'Unsupported video URL — use a YouTube or Vimeo link' : 'Video URL not set'}
          </div>
        );
      }
      const [ratioW, ratioH] = (block.aspectRatio ?? '16:9').split(':').map(Number);
      return (
        <div
          key={blockId}
          style={{
            ...vidMarginStyle,
            ...(block.maxHeight ? { maxHeight: block.maxHeight, overflow: 'hidden' } : {}),
          }}
        >
          <div style={{ position: 'relative', paddingTop: `${(ratioH / ratioW) * 100}%` }}>
            <iframe
              src={embedUrl}
              title={block.caption ?? 'Video'}
              style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', border: 'none' }}
              allow='accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture'
              allowFullScreen
            />
          </div>
          {block.caption && <p className={styles.mediaCaption}>{block.caption}</p>}
        </div>
      );
    }

    if (block.type === 'header') {
      return (
        <div
          key={blockId}
          style={{
            fontSize: block.fontSize ?? 30,
            color: block.color || undefined,
            marginTop: block.marginTop ?? 8,
            marginBottom: block.marginBottom ?? 8,
            fontWeight: 700,
            lineHeight: 1.25,
          }}
        >
          {block.text}
        </div>
      );
    }

    if (block.type === 'divider') {
      return (
        <div
          key={blockId}
          style={{
            display: 'flex',
            justifyContent: 'center',
            marginTop: block.marginTop ?? 16,
            marginBottom: block.marginBottom ?? 16,
          }}
        >
          <div
            style={{
              width: `${block.lengthPercent ?? 100}%`,
              borderTop: `${block.thickness ?? 1}px ${block.lineType ?? 'solid'} ${block.color ?? '#e2e8f0'}`,
            }}
          />
        </div>
      );
    }

    return null;
  };

  const wrapBlock = (
    blockId: string,
    inner: React.ReactNode,
    sectionId?: string,
    blockIndex?: number,
    totalBlocks?: number,
    halfWidth?: boolean,
    prevHalfWidth?: boolean,
  ): React.ReactNode => {
    if (!inner) return null;
    const isHighlighted = highlightedBlockId === blockId;
    const isBuilderSelected = builderConfig !== undefined && builderConfig.selectedBlockKey === blockId;

    if (builderConfig && sectionId !== undefined && blockIndex !== undefined && totalBlocks !== undefined) {
      const bc = builderConfig;
      // Between two consecutive halfWidth blocks, render an inline (zero-width) drop zone that
      // doesn't disrupt the side-by-side row layout.  All other drop zones are full-width.
      const makeDropZone = (atIndex: number, inline = false) => (
        <DropZone
          key={`dz-${sectionId}-${atIndex}`}
          className={inline ? `${styles.builderDropZone} ${styles.builderDropZoneInline}` : styles.builderDropZone}
          label='+ Drop Here'
          onDrop={(bt) => bc.onDropBlock(sectionId, bt, atIndex)}
          suppressed={blockDragKey !== null}
        />
      );

      const isDraggingThisBlock = blockDragKey === blockId;
      const isDragOverThis = blockDragOverKey === blockId;

      return (
        <React.Fragment key={blockId}>
          {makeDropZone(blockIndex, !!(halfWidth && prevHalfWidth))}
          <div
            className={[
              styles.blockWrap,
              styles.builderBlock,
              isHighlighted ? styles.blockHighlighted : '',
              isBuilderSelected ? styles.builderSelected : '',
              halfWidth ? styles.halfWidth : '',
              halfWidth && prevHalfWidth ? styles.halfWidthRight : '',
              isDraggingThisBlock ? styles.blockDraggingSource : '',
              isDragOverThis && blockDragOverHalf === 'top' ? styles.blockDragOverTop : '',
              isDragOverThis && blockDragOverHalf === 'bottom' ? styles.blockDragOverBottom : '',
              isDragOverThis && blockDragOverHalf === 'left' ? styles.blockDragOverLeft : '',
              isDragOverThis && blockDragOverHalf === 'right' ? styles.blockDragOverRight : '',
            ]
              .filter(Boolean)
              .join(' ')}
            draggable
            onDragStart={(e) => {
              e.dataTransfer.setData('blockDragSectionId', sectionId);
              e.dataTransfer.setData('blockDragIdx', String(blockIndex));
              e.dataTransfer.effectAllowed = 'move';
              blockDragRef.current = { sectionId, blockIndex, halfWidth: !!halfWidth };
              setBlockDragKey(blockId);
            }}
            onDragEnd={() => {
              blockDragRef.current = null;
              setBlockDragKey(null);
              setBlockDragOverKey(null);
            }}
            onClick={() => bc.onBlockClick(sectionId, blockIndex)}
            onDragOver={(e) => {
              if (halfWidth) {
                // HalfWidth blocks sit side-by-side; accept both palette and reorder drags,
                // and show a left/right indicator so the user sees where the block will land.
                const isPaletteDrag =
                  !blockDragRef.current &&
                  Array.from(e.dataTransfer.types).some((t) => t.toLowerCase() === 'blocktype');
                if (!blockDragRef.current && !isPaletteDrag) return;
                e.preventDefault();
                const rect = e.currentTarget.getBoundingClientRect();
                const half = e.clientX < rect.left + rect.width / 2 ? 'left' : 'right';
                setBlockDragOverKey(blockId);
                setBlockDragOverHalf(half);
              } else {
                // Full-width blocks: only handle canvas block-to-block reorders.
                // Palette drops go to the full-width DropZones above/below.
                if (!blockDragRef.current) return;
                e.preventDefault();
                const rect = e.currentTarget.getBoundingClientRect();
                const half = e.clientY < rect.top + rect.height / 2 ? 'top' : 'bottom';
                setBlockDragOverKey(blockId);
                setBlockDragOverHalf(half);
              }
            }}
            onDrop={(e) => {
              e.preventDefault();
              const rect = e.currentTarget.getBoundingClientRect();
              if (halfWidth) {
                // Left/right split for side-by-side blocks
                const xHalf = e.clientX < rect.left + rect.width / 2 ? 'left' : 'right';
                const rawToIdx = xHalf === 'left' ? blockIndex : blockIndex + 1;
                const blockType = e.dataTransfer.getData('blockType');
                if (blockType) {
                  bc.onDropBlock(sectionId, blockType, rawToIdx);
                  setBlockDragKey(null);
                  setBlockDragOverKey(null);
                  return;
                }
                const fromSectionId = e.dataTransfer.getData('blockDragSectionId');
                const fromIdx = parseInt(e.dataTransfer.getData('blockDragIdx'), 10);
                if (!fromSectionId || isNaN(fromIdx)) return;
                let toIdx = rawToIdx;
                if (fromSectionId === sectionId && fromIdx < toIdx) toIdx -= 1;
                // Adjacent right-to-left drop: source is already the immediate predecessor,
                // so toIdx === fromIdx after adjustment (no-op). Swap instead.
                if (fromSectionId === sectionId && toIdx === fromIdx) toIdx = rawToIdx;
                if (fromSectionId !== sectionId || fromIdx !== toIdx) {
                  bc.onBlockDragReorder(fromSectionId, fromIdx, sectionId, toIdx);
                }
              } else {
                // Top/bottom split for full-width blocks
                const fromSectionId = e.dataTransfer.getData('blockDragSectionId');
                const fromIdx = parseInt(e.dataTransfer.getData('blockDragIdx'), 10);
                if (!fromSectionId || isNaN(fromIdx)) return;
                const yHalf = e.clientY < rect.top + rect.height / 2 ? 'top' : 'bottom';
                let toIdx = yHalf === 'top' ? blockIndex : blockIndex + 1;
                if (fromSectionId === sectionId && fromIdx < toIdx) toIdx -= 1;
                if (fromSectionId !== sectionId || fromIdx !== toIdx) {
                  bc.onBlockDragReorder(fromSectionId, fromIdx, sectionId, toIdx);
                }
              }
              setBlockDragKey(null);
              setBlockDragOverKey(null);
            }}
          >
            <div className={styles.builderBlockOverlay}>
              {/* Drag handle */}
              <span className={styles.builderDragHandle} title='Drag to reorder'>
                ⠿
              </span>
              <button
                className={styles.builderOverlayBtn}
                title='Move up'
                disabled={blockIndex === 0}
                onClick={(e) => {
                  e.stopPropagation();
                  bc.onBlockMove(sectionId, blockIndex, 'up');
                }}
              >
                <svg
                  width='13'
                  height='13'
                  viewBox='0 0 24 24'
                  fill='none'
                  stroke='currentColor'
                  strokeWidth='2.5'
                  strokeLinecap='round'
                >
                  <path d='M12 19V5' />
                  <path d='M5 12l7-7 7 7' />
                </svg>
              </button>
              <button
                className={styles.builderOverlayBtn}
                title='Move down'
                disabled={blockIndex === totalBlocks - 1}
                onClick={(e) => {
                  e.stopPropagation();
                  bc.onBlockMove(sectionId, blockIndex, 'down');
                }}
              >
                <svg
                  width='13'
                  height='13'
                  viewBox='0 0 24 24'
                  fill='none'
                  stroke='currentColor'
                  strokeWidth='2.5'
                  strokeLinecap='round'
                >
                  <path d='M12 5v14' />
                  <path d='M19 12l-7 7-7-7' />
                </svg>
              </button>
              <button
                className={`${styles.builderOverlayBtn} ${styles.builderDuplicateBtn}`}
                title='Duplicate block'
                onClick={(e) => {
                  e.stopPropagation();
                  bc.onBlockDuplicate(sectionId, blockIndex);
                }}
              >
                <svg
                  width='13'
                  height='13'
                  viewBox='0 0 24 24'
                  fill='none'
                  stroke='currentColor'
                  strokeWidth='2.2'
                  strokeLinecap='round'
                >
                  <rect x='9' y='9' width='13' height='13' rx='2' />
                  <path d='M5 15H4a2 2 0 01-2-2V4a2 2 0 012-2h9a2 2 0 012 2v1' />
                </svg>
              </button>
              <button
                className={`${styles.builderOverlayBtn} ${styles.builderEditBtn}`}
                title='Edit block'
                onClick={(e) => {
                  e.stopPropagation();
                  bc.onBlockClick(sectionId, blockIndex);
                }}
              >
                <svg
                  width='13'
                  height='13'
                  viewBox='0 0 24 24'
                  fill='none'
                  stroke='currentColor'
                  strokeWidth='2.2'
                  strokeLinecap='round'
                >
                  <path d='M11 4H4a2 2 0 00-2 2v14a2 2 0 002 2h14a2 2 0 002-2v-7' />
                  <path d='M18.5 2.5a2.121 2.121 0 013 3L12 15l-4 1 1-4 9.5-9.5z' />
                </svg>
              </button>
              <button
                className={`${styles.builderOverlayBtn} ${styles.builderDeleteBtn}`}
                title='Delete block'
                onClick={(e) => {
                  e.stopPropagation();
                  bc.onBlockDelete(sectionId, blockIndex);
                }}
              >
                <svg
                  width='13'
                  height='13'
                  viewBox='0 0 24 24'
                  fill='none'
                  stroke='currentColor'
                  strokeWidth='2.2'
                  strokeLinecap='round'
                >
                  <polyline points='3 6 5 6 21 6' />
                  <path d='M19 6l-1 14H6L5 6' />
                  <path d='M10 11v6' />
                  <path d='M14 11v6' />
                  <path d='M9 6V4h6v2' />
                </svg>
              </button>
            </div>
            {inner}
          </div>
          {blockIndex === totalBlocks - 1 && makeDropZone(totalBlocks)}
        </React.Fragment>
      );
    }

    return (
      <div
        key={blockId}
        className={[
          styles.blockWrap,
          halfWidth ? styles.halfWidth : '',
          halfWidth && prevHalfWidth ? styles.halfWidthRight : '',
          isHighlighted ? styles.blockHighlighted : '',
        ]
          .filter(Boolean)
          .join(' ')}
        onClick={isHost && onHighlightBlock ? () => onHighlightBlock(isHighlighted ? null : blockId) : undefined}
        style={isHost && onHighlightBlock ? { cursor: 'pointer' } : undefined}
        title={isHost && onHighlightBlock ? 'Click to focus client attention here' : undefined}
      >
        {inner}
        {isHighlighted && isHost && <span className={styles.blockFocusTag}>Focusing {clientLabel.toLowerCase()}</span>}
      </div>
    );
  };

  const renderSection = (section: ConsentSection, idx: number) => {
    const isBuilder = builderConfig !== undefined;
    return (
      <section
        key={section.id}
        className={[styles.section, isBuilder ? styles.builderSection : ''].filter(Boolean).join(' ')}
        ref={(el) => {
          sectionRefs.current[section.id] = el;
        }}
      >
        {isBuilder && (
          <div className={styles.builderSectionActions}>
            <button
              className={styles.builderSectionMoveBtn}
              title='Move section up'
              disabled={idx === 0}
              onClick={() => builderConfig!.onSectionMove(section.id, 'up')}
            >
              ↑
            </button>
            <button
              className={styles.builderSectionMoveBtn}
              title='Move section down'
              disabled={idx === builderConfig!.totalSections - 1}
              onClick={() => builderConfig!.onSectionMove(section.id, 'down')}
            >
              ↓
            </button>
            {builderConfig!.onSectionEdit && (
              <button
                className={styles.builderSectionEditBtn}
                title='Edit section'
                onClick={() => builderConfig!.onSectionEdit!(section.id)}
              >
                <svg
                  width='12'
                  height='12'
                  viewBox='0 0 24 24'
                  fill='none'
                  stroke='currentColor'
                  strokeWidth='2.2'
                  strokeLinecap='round'
                >
                  <path d='M11 4H4a2 2 0 00-2 2v14a2 2 0 002 2h14a2 2 0 002-2v-7' />
                  <path d='M18.5 2.5a2.121 2.121 0 013 3L12 15l-4 1 1-4 9.5-9.5z' />
                </svg>
              </button>
            )}
            <button
              className={styles.builderSectionDeleteBtn}
              title='Delete section'
              onClick={() => builderConfig!.onSectionDelete(section.id)}
            >
              ✕
            </button>
          </div>
        )}
        {!section.hideSectionTitle && <h2>{section.sectionTitle || `Section ${idx + 1}`}</h2>}
        <h3>{applyLabel(section.heading)}</h3>
        <div className={styles.bodyText}>
          {section.blocks.length === 0 && isBuilder ? (
            <DropZone
              className={`${styles.builderDropZone} ${styles.builderDropZoneEmpty}`}
              label='Drop components here'
              onDrop={(bt) => builderConfig!.onDropBlock(section.id, bt, 0)}
            />
          ) : (
            section.blocks.map((block, bi) => {
              const bId = `${section.id}-${bi}`;
              const hwCheck = (b: ContentBlock) =>
                (b.type === 'field-group' && b.halfWidth === true) ||
                ((b.type === 'scale' ||
                  b.type === 'matrix' ||
                  b.type === 'time-field' ||
                  b.type === 'image' ||
                  b.type === 'video' ||
                  b.type === 'checklist') &&
                  (b as { halfWidth?: boolean }).halfWidth === true);
              const hw = hwCheck(block);
              // Compute position within consecutive halfWidth run (even=left, odd=right).
              // Scanning back avoids styling block 2 as "right" when it wraps to a new row.
              // A block with rowBreak:true acts as a run boundary — it and any successor
              // start fresh at run index 0.
              let runIdx = 0;
              if (hw && !(block as { rowBreak?: boolean }).rowBreak) {
                for (let j = bi - 1; j >= 0; j--) {
                  const b = section.blocks[j];
                  if (hwCheck(b) && !(b as { rowBreak?: boolean }).rowBreak) runIdx++;
                  else break;
                }
              }
              const isRight = hw && runIdx % 2 === 1;
              return wrapBlock(
                bId,
                renderBlock(block, bi, section.id),
                section.id,
                bi,
                section.blocks.length,
                hw,
                isRight,
              );
            })
          )}
        </div>
      </section>
    );
  };

  return (
    <div
      className={builderConfig ? styles.builderScrimOverride : styles.modalScrim}
      role='dialog'
      aria-modal='true'
      aria-label={definition.title}
    >
      <div
        className={[
          styles.modal,
          builderConfig ? styles.builderModalOverride : '',
          builderConfig && builderConfig.isMobilePreview ? styles.builderMobilePreview : '',
          !builderConfig && !isHost ? styles.clientModal : '',
          !builderConfig && isHost && clientViewMode && !clientViewport ? styles.clientViewModal : '',
          !builderConfig && isHost && clientViewMode && clientViewport && clientViewport.width <= 640
            ? styles.mobileView
            : '',
        ]
          .filter(Boolean)
          .join(' ')}
        style={
          !builderConfig && isHost && clientViewMode && clientViewport
            ? {
                width: Math.min(clientViewport.width, window.innerWidth - 64),
                height: Math.min(clientViewport.height, window.innerHeight - 64),
              }
            : undefined
        }
      >
        {/* PREVIEW badge */}
        {(preview || isBlankPreview) && <div className={styles.previewBadge}>PREVIEW</div>}

        {/* HOST — mode switch + progress pill (top-right); hidden once consent is received */}
        {isHost && !preview && !submitted && onModeChange && (
          <div className={styles.modeSwitch}>
            <div className={styles.modeSwitchRow}>
              <span className={styles.modeSwitchLabel}>Mode</span>
              <div className={styles.modeSwitchTrack}>
                <button
                  title='Scroll at your own pace.'
                  className={`${styles.modeSwitchBtn} ${mode === 'selfpaced' ? styles.modeSwitchActive : ''}`}
                  onClick={() => handleModeChange('selfpaced')}
                >
                  <svg width='11' height='11' viewBox='0 0 24 24' fill='none' stroke='currentColor' strokeWidth='2.5'>
                    <circle cx='12' cy='8' r='4' />
                    <path d='M4 20c0-4 3.6-7 8-7s8 3 8 7' />
                  </svg>
                  <span className={styles.modeSwitchBtnText}>Self-Paced</span>
                </button>
                <button
                  title='Host controls scrolling'
                  className={`${styles.modeSwitchBtn} ${mode === 'guided' ? styles.modeSwitchActive : ''}`}
                  onClick={() => handleModeChange('guided')}
                >
                  <svg width='11' height='11' viewBox='0 0 24 24' fill='none' stroke='currentColor' strokeWidth='2.5'>
                    <path d='M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z' />
                    <circle cx='12' cy='12' r='3' />
                  </svg>
                  <span className={styles.modeSwitchBtnText}>Guided</span>
                </button>
                <button
                  title='Client controls scrolling'
                  className={`${styles.modeSwitchBtn} ${mode === 'follow' ? styles.modeSwitchActive : ''}`}
                  onClick={() => handleModeChange('follow')}
                >
                  <svg width='11' height='11' viewBox='0 0 24 24' fill='none' stroke='currentColor' strokeWidth='2.5'>
                    <circle cx='9' cy='7' r='3' />
                    <path d='M2 21c0-4 3-7 7-7' />
                    <path d='M16 13l4 4-4 4' />
                    <path d='M12 17h8' />
                  </svg>
                  <span className={styles.modeSwitchBtnText}>Follow</span>
                </button>
              </div>
            </div>
            <div className={styles.pillRow}>
              <button
                className={`${styles.modeSwitchBtn} ${clientViewMode ? styles.modeSwitchActive : ''}`}
                onClick={toggleClientView}
                title={clientViewMode ? 'Disable client view' : 'Resize form to match client'}
              >
                <svg width='11' height='11' viewBox='0 0 24 24' fill='none' stroke='currentColor' strokeWidth='2.5'>
                  <rect x='2' y='4' width='20' height='13' rx='2' />
                  <path d='M8 21h8' />
                  <path d='M12 17v4' />
                </svg>
                <span className={styles.modeSwitchBtnText}>Client View</span>
              </button>
              <div className={styles.pillWrap}>
                <span
                  className={[styles.progressPill, pillFilledCount >= pillRequiredCount ? styles.progressPillDone : '']
                    .filter(Boolean)
                    .join(' ')}
                >
                  {pillFilledCount >= pillRequiredCount
                    ? '✓ All fields complete'
                    : `${pillRequiredCount - pillFilledCount} remaining`}
                </span>
                {remainingItems.length > 0 && (
                  <div className={styles.pillTooltip}>
                    {remainingItems.map(({ id, label }) => (
                      <div key={id}>{label}</div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        {/* CLIENT — Cancel Session button (top-right) */}
        {!isHost && !preview && !submitted && !builderConfig && (
          <div className={styles.clientCancelBtn}>
            <button className={`${styles.btn} ${styles.danger}`} onClick={() => setConfirmClose(true)}>
              Cancel Session
            </button>
          </div>
        )}

        {/* HEADER */}
        <div className={styles.mHead}>
          <div className={styles.reqRow}>
            {(() => {
              const displayWho = isHost
                ? participantName
                : providerName?.trim() || (fields['__providerName'] as string)?.trim() || participantName;
              if (!displayWho) return null;
              const TITLES = /^(dr\.?|mr\.?|mrs\.?|ms\.?|prof\.?|rev\.?|sir)$/i;
              const CREDENTIALS = /^[A-Z]{2,6}$/;
              const nameParts = displayWho
                .split(' ')
                .map((w) => w.replace(/[,.:]+$/, ''))
                .filter((w) => w && !TITLES.test(w) && !CREDENTIALS.test(w));
              const first = nameParts[0];
              const last = nameParts.length > 1 ? nameParts[nameParts.length - 1] : null;
              const initials = [first, last]
                .filter(Boolean)
                .map((w) => w![0].toUpperCase())
                .join('');
              return (
                <span className={styles.who}>
                  <span className={styles.av}>{initials}</span>
                  {displayWho}
                </span>
              );
            })()}
          </div>
          <h1 ref={titleRef} className={styles.formTitle}>
            {applyLabel(definition.title)}
          </h1>
          <div className={styles.sub}>
            <svg className={styles.lockIcon} viewBox='0 0 24 24' fill='none' stroke='currentColor' strokeWidth='2'>
              <rect x='4' y='11' width='16' height='10' rx='2' />
              <path d='M8 11V7a4 4 0 018 0v4' />
            </svg>
            {definition.headerSubtitle?.trim() || 'Please read carefully and sign.'}
          </div>
        </div>

        {/* SECTION NAV */}
        <div className={styles.navWrap}>
          {navCanScrollLeft && !scrollLocked && !submitted && (
            <button
              className={`${styles.navArrow} ${styles.navArrowLeft}`}
              onClick={() => scrollNav('left')}
              aria-label='Scroll sections left'
              tabIndex={-1}
            >
              <svg
                width='9'
                height='14'
                viewBox='0 0 9 14'
                fill='none'
                stroke='currentColor'
                strokeWidth='2.2'
                strokeLinecap='round'
                strokeLinejoin='round'
              >
                <path d='M7 1L2 7l5 6' />
              </svg>
            </button>
          )}

          <nav
            className={[
              styles.secNav,
              scrollLocked ? styles.secNavLocked : '',
              navCanScrollLeft ? styles.secNavPadLeft : '',
              navCanScrollRight ? styles.secNavPadRight : '',
            ]
              .filter(Boolean)
              .join(' ')}
            aria-label='Sections'
            ref={navRef}
          >
            {definition.sections.map((s, i) => {
              const navBtnClass = [activeSection === s.id ? styles.active : '', sectionDone(s) ? styles.done : '']
                .filter(Boolean)
                .join(' ');
              const numSpan = (
                <span className={styles.num}>
                  <span>{i + 1}</span>
                </span>
              );
              if (builderConfig?.onNavLabelChange && editingNavId === s.id) {
                return (
                  <div
                    key={s.id}
                    ref={(el) => {
                      navBtnRefs.current[s.id] = el as unknown as HTMLButtonElement;
                    }}
                    className={[styles.navEditingWrapper, navBtnClass].filter(Boolean).join(' ')}
                  >
                    {numSpan}
                    <div className={styles.navInputSizer} data-replicated-value={editingNavLabel}>
                      <input
                        autoFocus
                        className={styles.navLabelInlineInput}
                        value={editingNavLabel}
                        onChange={(e) => setEditingNavLabel(e.target.value)}
                        onBlur={() => {
                          builderConfig.onNavLabelChange!(s.id, editingNavLabel);
                          setEditingNavId(null);
                        }}
                        onKeyDown={(e) => {
                          e.stopPropagation();
                          if (e.key === 'Enter') {
                            builderConfig.onNavLabelChange!(s.id, editingNavLabel);
                            setEditingNavId(null);
                          } else if (e.key === 'Escape') {
                            setEditingNavId(null);
                          }
                        }}
                      />
                    </div>
                  </div>
                );
              }
              return (
                <button
                  key={s.id}
                  ref={(el) => {
                    navBtnRefs.current[s.id] = el;
                  }}
                  className={navBtnClass}
                  onClick={() => {
                    if (builderConfig?.onNavLabelChange) {
                      setEditingNavId(s.id);
                      setEditingNavLabel(s.navLabel);
                      return;
                    }
                    jumpTo(s.id);
                  }}
                  disabled={scrollLocked}
                  tabIndex={scrollLocked ? -1 : undefined}
                >
                  {numSpan}
                  {applyLabel(s.navLabel)}
                </button>
              );
            })}
          </nav>

          {navCanScrollRight && !scrollLocked && !submitted && (
            <button
              className={`${styles.navArrow} ${styles.navArrowRight}`}
              onClick={() => scrollNav('right')}
              aria-label='Scroll sections right'
              tabIndex={-1}
            >
              <svg
                width='9'
                height='14'
                viewBox='0 0 9 14'
                fill='none'
                stroke='currentColor'
                strokeWidth='2.2'
                strokeLinecap='round'
                strokeLinejoin='round'
              >
                <path d='M2 1l5 6-5 6' />
              </svg>
            </button>
          )}

          {/* Scroll-locked notification bar — client in guided, host in follow */}
          {scrollLocked && !submitted && (
            <div className={styles.guidedBar}>
              <svg width='13' height='13' viewBox='0 0 24 24' fill='none' stroke='currentColor' strokeWidth='2.5'>
                <rect x='4' y='11' width='16' height='10' rx='2' />
                <path d='M8 11V7a4 4 0 018 0v4' />
              </svg>
              Scrolling has been disabled
            </div>
          )}
        </div>

        {/* BODY */}
        <div className={styles.mBody} ref={bodyRef}>
          {definition.intro && (
            <div className={styles.introPill}>
              <svg
                className={styles.introPillIcon}
                viewBox='0 0 24 24'
                fill='none'
                stroke='currentColor'
                strokeWidth='2'
              >
                <path d='M12 2l8 4v6c0 5-3.5 9-8 10-4.5-1-8-5-8-10V6l8-4z' />
              </svg>
              <span>{renderIntro(definition.intro)}</span>
            </div>
          )}
          {definition.sections.map(renderSection)}
        </div>

        {/* VALIDATION BANNER */}
        {showErr && filledCount < requiredCount && (
          <div className={styles.valBanner}>
            <svg width='14' height='14' viewBox='0 0 24 24' fill='none' stroke='currentColor' strokeWidth='2'>
              <circle cx='12' cy='12' r='10' />
              <path d='M12 8v4' />
              <path d='M12 16h.01' />
            </svg>
            Please complete all required fields before submitting.
          </div>
        )}

        {/* BLANK PREVIEW FOOTER */}
        {isBlankPreview && (
          <div className={styles.mFoot}>
            <div className={styles.secure}>
              <svg width='14' height='14' viewBox='0 0 24 24' fill='none' stroke='currentColor' strokeWidth='2'>
                <path d='M12 2l8 4v6c0 5-3.5 9-8 10-4.5-1-8-5-8-10V6l8-4z' />
              </svg>
              HIPAA-compliant · End-to-end encrypted
            </div>
            {!builderConfig && (
              <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                <button type='button' className={styles.btn} onClick={() => onClose?.()}>
                  Close
                </button>
                <button
                  type='button'
                  className={`${styles.btn} ${styles.primary}`}
                  onClick={handleDownloadBlank}
                  disabled={downloadingBlank}
                >
                  {downloadingBlank ? (
                    <span className={styles.btnSpinner} />
                  ) : (
                    <svg width='14' height='14' viewBox='0 0 24 24' fill='none' stroke='currentColor' strokeWidth='2.2'>
                      <path d='M12 4v12' />
                      <path d='M7 11l5 5 5-5' />
                      <path d='M5 20h14' />
                    </svg>
                  )}
                  {downloadingBlank ? 'Preparing PDF…' : 'Download PDF Copy'}
                </button>
              </div>
            )}
          </div>
        )}

        {/* FOOTER */}
        {!preview && !isBlankPreview && (
          <div className={styles.mFoot}>
            <div className={styles.secure}>
              <svg width='14' height='14' viewBox='0 0 24 24' fill='none' stroke='currentColor' strokeWidth='2'>
                <path d='M12 2l8 4v6c0 5-3.5 9-8 10-4.5-1-8-5-8-10V6l8-4z' />
              </svg>
              HIPAA-compliant · End-to-end encrypted
            </div>
            {isHost ? (
              <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                <button className={`${styles.btn} ${styles.danger}`} onClick={() => setConfirmClose(true)}>
                  End Teleconsent Session
                </button>
                {filledCount >= requiredCount && (
                  <button className={`${styles.btn} ${styles.primary}`} onClick={trySubmit} disabled={submitting}>
                    {submitting ? 'Submitting…' : 'Finalize'}
                    <svg width='14' height='14' viewBox='0 0 24 24' fill='none' stroke='currentColor' strokeWidth='2.5'>
                      <path d='M5 13l4 4L19 7' />
                    </svg>
                  </button>
                )}
              </div>
            ) : (
              <button
                className={`${styles.btn} ${styles.primary}`}
                onClick={trySubmit}
                disabled={filledCount < requiredCount || submitting}
              >
                {submitting ? 'Submitting…' : 'Submit & Finalize'}
                <svg width='14' height='14' viewBox='0 0 24 24' fill='none' stroke='currentColor' strokeWidth='2.5'>
                  <path d='M5 12h14' />
                  <path d='M13 6l6 6-6 6' />
                </svg>
              </button>
            )}
          </div>
        )}

        {/* POST-SUBMIT OVERLAY */}
        {submitted && (
          <div className={styles.doneState}>
            <div className={styles.check}>
              <svg width='32' height='32' viewBox='0 0 24 24' fill='none' stroke='currentColor' strokeWidth='3'>
                <path d='M5 13l4 4L19 7' />
              </svg>
            </div>
            <h2>Consent received</h2>
            <p className={styles.doneLead}>Download the completed consent form for your records.</p>
            <p className={styles.doneFine}>
              For privacy and security, HIPAA LINK does not store copies of completed forms after submission. Please
              save your copy before leaving this page.
            </p>

            <button type='button' className={styles.dlBtn} onClick={handleDownload} disabled={downloadingPdf}>
              {downloadingPdf ? (
                <span className={styles.btnSpinner} />
              ) : (
                <svg width='16' height='16' viewBox='0 0 24 24' fill='none' stroke='currentColor' strokeWidth='2.2'>
                  <path d='M12 4v12' />
                  <path d='M7 11l5 5 5-5' />
                  <path d='M5 20h14' />
                </svg>
              )}
              {downloadingPdf ? 'Preparing PDF…' : 'Download PDF'}
            </button>

            {finalReceipt && (
              <div className={styles.receipt}>
                Signed {new Date(finalReceipt.submittedAt).toLocaleString()} · IP {finalReceipt.clientIp}
              </div>
            )}

            {isHost && <hr className={styles.doneDivider} />}

            {isHost && (
              <div className={`${styles.dlCardPill} ${clientDownloaded ? styles.ok : styles.warn}`}>
                {clientDownloaded ? 'Client has downloaded form.' : 'Client has not downloaded the form.'}
              </div>
            )}

            {isHost && (
              <div className={styles.doneActions}>
                <button
                  className={styles.btn}
                  onClick={() => {
                    setSubmitted(false);
                    onGoBack?.();
                  }}
                >
                  ← Back
                </button>
                <button className={`${styles.btn} ${styles.danger}`} onClick={() => setConfirmClose(true)}>
                  End Teleconsent Session
                </button>
              </div>
            )}
          </div>
        )}

        {/* MODE CHANGE SNACKBAR */}
        {snackbar && <div className={styles.snackbar}>{snackbar}</div>}

        {/* CLIENT ENDED SESSION NOTICE — host only */}
        {isHost && clientEndedSession && (
          <div className={styles.confirmScrim}>
            <div className={styles.confirmCard} onClick={(e) => e.stopPropagation()}>
              <div className={styles.confirmIcon}>
                <svg width='22' height='22' viewBox='0 0 24 24' fill='none' stroke='currentColor' strokeWidth='2'>
                  <path d='M12 9v4' />
                  <path d='M12 17h.01' />
                  <circle cx='12' cy='12' r='10' />
                </svg>
              </div>
              <h3>Client Ended Session</h3>
              <p>The client has closed the Teleconsent session.</p>
              <div className={styles.confirmActions}>
                <button className={`${styles.btn} ${styles.primary}`} onClick={() => onDismissClientEnded?.()}>
                  OK
                </button>
              </div>
            </div>
          </div>
        )}

        {/* END SESSION CONFIRMATION */}
        {confirmClose && (
          <div className={styles.confirmScrim} onClick={() => setConfirmClose(false)}>
            <div className={styles.confirmCard} onClick={(e) => e.stopPropagation()}>
              <div className={styles.confirmIcon}>
                <svg width='22' height='22' viewBox='0 0 24 24' fill='none' stroke='currentColor' strokeWidth='2'>
                  <path d='M12 9v4' />
                  <path d='M12 17h.01' />
                  <circle cx='12' cy='12' r='10' />
                </svg>
              </div>
              <h3>Are you sure?</h3>
              <p>
                {submitted
                  ? 'Download your PDF before closing. Data is not stored and cannot be recovered.'
                  : isHost
                  ? 'All form data will be discarded and cannot be recovered (data is not stored).'
                  : 'Your form will not be saved.'}
              </p>
              <div className={styles.confirmActions}>
                <button className={styles.btn} onClick={() => setConfirmClose(false)}>
                  Cancel
                </button>
                <button className={`${styles.btn} ${styles.danger}`} onClick={() => onClose?.()}>
                  Confirm
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
